import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";

import {
  Archive,
  Calendar,
  BarChart3,
  Building2,
  ClipboardX,
  Loader2,
  UserRound,
  Users,
  X,
  Info,
} from "lucide-react";
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip } from "recharts";

import Badge from "../../Components/Badge";
import DatePicker from "../../Components/DatePicker";
import EngagementEmptyPanel from "../../Components/EngagementEmptyPanel";
import CommonModal from "../../Components/Modal";
import TabsComponent from "../../Components/Tab";
import { useToast } from "../../Components/Toast";
import ToggleSwitch from "../../Components/ToggleSwitch";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { fetchOrganizationLevels } from "../../Store/Reducers/AdminSlice";
import {
  checkFormalRunning,
  createAssessment,
  fetchAssessmentAccountsTeamsBatch,
  fetchAssessmentQuestionsAndResponses,
  fetchLevelMix,
  fetchLevelMixSnapshot,
  fetchAssessments,
  LevelMixLevel,
  LevelMixOrigin,
  LevelMixScope,
  PublishedColumnStatus,
  type FormalRunningAccount,
} from "../../Store/Reducers/AssessmentSlice";
import { fetchDisconnectFlowConfig } from "../../Store/Reducers/VendorAdminSlice";
import {
  formatAssessmentDateOrdinalUtc,
  formatAssessmentDateTimeUtc,
  parseAssessmentDate,
  toUtcDateInputValue,
  utcMonthsAgoInputValue,
  utcTodayInputValue,
} from "../../Utils/Helper/assessmentDateUtils";
import { buildDefaultFormalDueOn } from "../../Utils/Helper/assessmentDueDefaults";
import {
  isPublishedFormalOrigin,
  isPublishedOrgMixScope,
  isPublishedSelfOrigin,
  normalizeLevelMixScope,
  normalizeTeamAssessmentDisplayName,
  pickPublishedLevelMixScope,
} from "../../Utils/Helper/assessmentOriginHelpers";
import { getInitials } from "../../Utils/Helper/Helper";
import {
  getAccountLevelCompactLabel,
  getLevelBadgeColor,
  getLevelCompactName,
  parseLevelNumber,
} from "../../Utils/Helper/levelUtils";
import {
  getFromLocalStorage,
  STORAGE_KEYS,
} from "../../Utils/Helper/LocalStorage";
import ConsolidatedReportModal from "../AssessmentsContainer/ConsolidatedReportModal";
import FormalRunningConflictModal from "../AssessmentsContainer/FormalRunningConflictModal";
import StartFormalRoundModal, {
  buildDefaultFormalDescription,
  buildDefaultFormalName,
  type FormalRoundTarget,
} from "../AssessmentsContainer/StartFormalRoundModal";

import "./style.scss";

const ORIGIN_LABELS: Record<LevelMixOrigin, string> = {
  FORMAL: "FORMAL",
  SELF: "TEAM",
  DISCONNECT_VIA_FORMAL: "Disconnect via Formal",
  DISCONNECT_VIA_SELF: "Disconnect via Team",
};

const publishedStatusCopy = (
  status?: PublishedColumnStatus | null,
  publishedLevel?: string | null
): string => {
  if (status === "awaiting_publish") return "Awaiting publish";
  if (status === "ongoing") return "Ongoing";
  if (status === "published") {
    return publishedLevel ? publishedLevel : "Published";
  }
  return publishedLevel || "";
};

const OriginStamp: React.FC<{ origin?: LevelMixOrigin | string | null }> = ({
  origin,
}) => {
  if (!origin || !(origin in ORIGIN_LABELS)) return null;
  const key = origin as LevelMixOrigin;
  const kind =
    key === "SELF"
      ? "is-self"
      : key.startsWith("DISCONNECT")
        ? "is-disconnect"
        : "is-formal";
  return (
    <span className={`level-mix-origin-stamp ${kind}`}>
      {ORIGIN_LABELS[key]}
    </span>
  );
};

const toDateInputValue = (isoDate?: string): string =>
  toUtcDateInputValue(isoDate);

/** Inclusive UTC day bounds for Level Mix date-range filtering. */
const parseUtcDateBound = (value: string, endOfDayFlag = false): Date => {
  const day = toUtcDateInputValue(value);
  if (!day) {
    const fallback = parseAssessmentDate(value);
    return fallback ?? new Date(NaN);
  }
  return endOfDayFlag
    ? new Date(`${day}T23:59:59.999Z`)
    : new Date(`${day}T00:00:00.000Z`);
};

const isoFromDateInput = (value: string): string => {
  if (!value) return "";
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  const [year, month, day] = value.split(/[-/]/).map(Number);
  if (!year || !month || !day) return "";
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

const normalizeIncompleteAccounts = (accounts: any[] = []) =>
  accounts.map((a: any) => ({
    ...a,
    respondedCount: Number.isFinite(a?.respondedCount) ? a.respondedCount : 0,
    totalAssigned:
      Number.isFinite(a?.totalAssigned) && a.totalAssigned > 0
        ? a.totalAssigned
        : 1,
    missingExecutives: Array.isArray(a?.missingExecutives)
      ? a.missingExecutives
      : [],
    hasPriorLevel: Boolean(a?.hasPriorLevel),
  }));

interface LevelMixContainerProps {
  orgId?: string | null;
  defaultDateRange?: {
    startDate: string;
    endDate: string;
  } | null;
  showPortfolioFilter?: boolean;
  allowedScopes?: LevelMixScope[];
  defaultScope?: LevelMixScope | "org";
  mixScopeLocked?: LevelMixScope | "org";
  /** Deep link: force scope on mount (raw URL value; normalized internally). */
  initialMixScope?: LevelMixScope | "org" | string | null;
  highlightAccountId?: string | null;
  onMixScopeChange?: (scope: LevelMixScope) => void;
}

// Use types from AssessmentSlice
type LevelMixItem = LevelMixLevel;
type AccountItem = LevelMixLevel["accounts"][0];

interface Assessment {
  id: string;
  assessment_id?: string;
  title?: string;
  status: string;
  publishedAt?: string;
  published_at?: string;
  notes?: {
    level_update_log?: Array<{
      at?: string | null;
      type?: string | null;
      account_id?: string | null;
      account_name?: string | null;
    }> | null;
  } | null;
}

const publishedAtMs = (assessment: Assessment): number => {
  const raw = assessment.publishedAt || assessment.published_at || "";
  const parsed = new Date(raw).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const sortPublishedNewestFirst = (assessments: Assessment[]): Assessment[] =>
  [...assessments].sort((a, b) => publishedAtMs(b) - publishedAtMs(a));

const dateRangeSpanningLastPublished = (
  assessments: Assessment[],
  count = 3
): { startDate: string; endDate: string } | null => {
  if (!assessments.length) return null;
  const sorted = sortPublishedNewestFirst(assessments);
  const latest = sorted[0];
  const oldestOfWindow = sorted[Math.min(count - 1, sorted.length - 1)];
  const startDate = toDateInputValue(
    oldestOfWindow.publishedAt || oldestOfWindow.published_at || ""
  );
  const endDate = toDateInputValue(
    latest.publishedAt || latest.published_at || ""
  );
  if (!startDate || !endDate) return null;
  return { startDate, endDate };
};

const uniqueAssessmentsFromMix = (
  levels: Array<{ accounts?: Array<Record<string, any>> }> | undefined,
  options?: { myScopeOnly?: boolean; currentLevelOnly?: boolean }
): Assessment[] => {
  const byId = new Map<string, Assessment>();
  (levels || []).forEach(levelItem => {
    (levelItem.accounts || []).forEach(acc => {
      if (options?.myScopeOnly) {
        if (acc.isCurrentLevel === false) return;
        if (acc.countsTowardMix === false) return;
        if (!acc.youSubmittedAt && !acc.you_submitted_at) return;
      } else if (options?.currentLevelOnly && acc.isCurrentLevel === false) {
        return;
      }
      const id = String(acc.assessmentId || acc.assessment_id || "");
      if (!id) return;
      const ts =
        acc.youSubmittedAt ||
        acc.you_submitted_at ||
        acc.publishedAt ||
        acc.published_at ||
        "";
      const existing = byId.get(id);
      const existingMs = existing ? publishedAtMs(existing) : 0;
      const nextMs = ts ? new Date(ts).getTime() : 0;
      if (!existing || nextMs >= existingMs) {
        byId.set(id, {
          id,
          assessment_id: id,
          title: normalizeTeamAssessmentDisplayName(
            acc.assessmentName || acc.assessment_name,
            "Untitled"
          ),
          publishedAt: ts,
          published_at: ts,
          status: acc.publishedStatus || "submitted",
        });
      }
    });
  });
  return sortPublishedNewestFirst(Array.from(byId.values()));
};

const normalizeRecentAssessments = (
  recent?: Array<Record<string, any>> | null
): Assessment[] => {
  const byId = new Map<string, Assessment>();
  (recent || []).forEach(row => {
    const id = String(row.id || row.assessmentId || row.assessment_id || "");
    if (!id) return;
    const ts = row.publishedAt || row.published_at || "";
    byId.set(id, {
      id,
      assessment_id: id,
      title: normalizeTeamAssessmentDisplayName(row.title, "Untitled"),
      publishedAt: ts,
      published_at: ts,
      status: row.status || "published",
    });
  });
  return sortPublishedNewestFirst(Array.from(byId.values()));
};

const mergeAssessmentsById = (...lists: Assessment[][]): Assessment[] => {
  const byId = new Map<string, Assessment>();
  lists.flat().forEach(a => {
    if (!a?.id) return;
    const existing = byId.get(a.id);
    if (!existing || publishedAtMs(a) >= publishedAtMs(existing)) {
      byId.set(a.id, a);
    }
  });
  return sortPublishedNewestFirst(Array.from(byId.values()));
};

/** Rewrite legacy Self Serve titles on level-mix account rows. */
const normalizeLevelMixLevels = (levels: LevelMixItem[]): LevelMixItem[] =>
  (levels || []).map(level => ({
    ...level,
    accounts: (level.accounts || []).map(acc => ({
      ...acc,
      assessmentName: normalizeTeamAssessmentDisplayName(
        acc.assessmentName ||
          (acc as { assessment_name?: string }).assessment_name,
        acc.assessmentName || "Untitled Assessment"
      ),
    })),
  }));

const isUnknownLevelMixRow = (level?: string | number | null): boolean =>
  String(level ?? "")
    .trim()
    .toLowerCase() === "unknown";

/** L0–L5 pie / % only — Unknown is table + Unassessed KPI, never pie share. */
const levelMixRowsForPie = (levels: LevelMixItem[]): LevelMixItem[] =>
  (levels || []).filter(l => !isUnknownLevelMixRow(l.level));

const sortLevelMixRows = (a: LevelMixItem, b: LevelMixItem): number => {
  if (isUnknownLevelMixRow(a.level)) return -1;
  if (isUnknownLevelMixRow(b.level)) return 1;
  return parseLevelNumber(a.level) - parseLevelNumber(b.level);
};

interface ChartDataPoint {
  date: string;
  dateOnly: string;
  level: number;
  timestamp: number;
  assessmentName: string;
  publishedAt: string;
}

interface TimelineGridProps {
  accountName: string;
  timelineData: {
    from: {
      level: number;
      date: string;
      publishedAt: string;
      assessmentName: string;
    };
    to: {
      level: number;
      date: string;
      publishedAt: string;
      assessmentName: string;
    };
  };
  allDataPoints: ChartDataPoint[];
  formatDateFn: (dateString: string) => string;
  organizationLevels?: Array<{ level_code: string; name: string }>;
}

// Tooltip Row Component
interface TooltipRowProps {
  icon: React.ReactNode;
  label: string;
  value: string | React.ReactNode;
  isLast?: boolean;
}

const TooltipRow: React.FC<TooltipRowProps> = ({
  icon,
  label,
  value,
  isLast = false,
}) => (
  <div className={`flex items-center gap-2 ${isLast ? "" : "mb-2"}`}>
    {icon}
    <span className="text-xs text-gray-700">
      <span className="font-medium">{label}:</span>{" "}
      <span className="font-bold">{value}</span>
    </span>
  </div>
);

const formatDateWithOrdinal = (
  dateString: string,
  formatDateFn: (date: string) => string
): string => {
  const formatted = formatAssessmentDateOrdinalUtc(dateString);
  return formatted || formatDateFn(dateString);
};

// Tooltip Component for profile hover
interface ProfileTooltipProps {
  publishedAt: string;
  assessmentName: string;
  level: number;
  columnIndex?: number;
  totalColumns?: number;
  mixScope?: LevelMixScope;
  youLevel?: string | null;
  publishedLevel?: string | null;
  publishedStatus?: PublishedColumnStatus | null;
  assessmentOrigin?: LevelMixOrigin | string | null;
}

const ProfileTooltip: React.FC<ProfileTooltipProps> = ({
  publishedAt,
  assessmentName,
  level,
  columnIndex = 0,
  totalColumns = 1,
  mixScope = "formal",
  youLevel,
  publishedLevel,
  publishedStatus,
  assessmentOrigin,
}) => {
  const isFirstColumn = columnIndex === 0;
  const positionClass = isFirstColumn ? "left-full ml-2" : "right-full mr-2";

  return (
    <div
      className={`absolute ${positionClass} top-1/2 -translate-y-1/2 w-64 bg-purple-50 border-l-4 border-purple-500 rounded-lg shadow-lg p-3 z-[100] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none whitespace-normal`}
    >
      <TooltipRow
        icon={<Calendar className="w-4 h-4 text-purple-600 flex-shrink-0" />}
        label="Published On"
        value={formatAssessmentDateTimeUtc(publishedAt, true)}
      />
      <TooltipRow
        icon={
          <svg
            className="w-4 h-4 text-purple-600 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        }
        label="Assessment"
        value={normalizeTeamAssessmentDisplayName(assessmentName, "N/A")}
      />
      <TooltipRow
        icon={
          <svg
            className="w-4 h-4 text-purple-600 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
            />
          </svg>
        }
        label="Account Level"
        value={
          <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-bold">
            {getAccountLevelCompactLabel(level)}
          </span>
        }
        isLast={mixScope !== "my"}
      />
      {mixScope === "my" && (
        <>
          <TooltipRow
            icon={<Info className="w-4 h-4 text-purple-600 flex-shrink-0" />}
            label="You"
            value={youLevel ? getLevelCompactName(youLevel) : "—"}
          />
          <TooltipRow
            icon={<Info className="w-4 h-4 text-purple-600 flex-shrink-0" />}
            label="Published"
            value={publishedStatusCopy(publishedStatus, publishedLevel) || "—"}
            isLast={!assessmentOrigin}
          />
          {assessmentOrigin ? (
            <TooltipRow
              icon={<Info className="w-4 h-4 text-purple-600 flex-shrink-0" />}
              label="Origin"
              value={<OriginStamp origin={assessmentOrigin} />}
              isLast
            />
          ) : null}
        </>
      )}
    </div>
  );
};

// Cell Content Component for From/To/Main points
interface CellContentProps {
  type: "from" | "to" | "main";
  accountName: string;
  publishedAt: string;
  assessmentName: string;
  level: number;
  formatDateFn: (date: string) => string;
  isLastRow?: boolean;
  columnIndex?: number;
  totalColumns?: number;
}

const CELL_CONFIG = {
  from: {
    logoClass:
      "w-6 h-6 rounded-full flex items-center justify-center text-[0.5rem] font-bold border border-blue-500 bg-blue-500 shadow-sm cursor-pointer",
    textColor: "text-white",
    label: null,
  },
  to: {
    logoClass:
      "w-6 h-6 rounded-full flex items-center justify-center text-[0.5rem] font-bold border border-blue-500 bg-blue-500 shadow-sm cursor-pointer",
    textColor: "text-white",
    label: null,
  },
  main: {
    logoClass:
      "w-6 h-6 rounded-full flex items-center justify-center bg-white border border-gray-400 shadow-sm cursor-pointer",
    textColor: "text-[0.5rem] font-bold text-gray-600",
    label: null,
  },
} as const;

const CellContent: React.FC<CellContentProps> = ({
  type,
  accountName,
  publishedAt,
  assessmentName,
  level,
  formatDateFn,
  isLastRow = false,
  columnIndex = 0,
  totalColumns = 1,
}) => {
  const config = CELL_CONFIG[type];
  const showDate = type === "from" || type === "to";
  const isMainType = type === "main";

  return (
    <div
      className="flex flex-col items-center gap-0.5 text-center relative group"
      style={{ minWidth: 0, width: "100%", overflow: "visible" }}
    >
      {config.label && (
        <div className="text-[0.625rem] font-bold text-gray-800 whitespace-nowrap leading-none">
          {config.label}
        </div>
      )}
      {isMainType ? (
        <div className="relative" style={{ overflow: "visible" }}>
          <div className="w-2 h-2 rounded-full bg-gray-400 cursor-pointer relative z-10"></div>
          <ProfileTooltip
            publishedAt={publishedAt}
            assessmentName={assessmentName}
            level={level}
            columnIndex={columnIndex}
            totalColumns={totalColumns}
          />
        </div>
      ) : (
        <div
          className={`${config.logoClass} relative`}
          style={{ overflow: "visible" }}
        >
          <span className={config.textColor}>{getInitials(accountName)}</span>
          <ProfileTooltip
            publishedAt={publishedAt}
            assessmentName={assessmentName}
            level={level}
            columnIndex={columnIndex}
            totalColumns={totalColumns}
          />
        </div>
      )}
      {showDate && (
        <div className="font-medium text-[0.5rem] text-gray-700 rounded whitespace-nowrap leading-none">
          {formatDateWithOrdinal(publishedAt, formatDateFn)}
        </div>
      )}
    </div>
  );
};

// Build SVG arrow path between timeline markers (arcs flat same-level transitions).
const buildTimelineArrowPath = (
  points: Array<{ x: number; y: number }>,
  markerPad: number
): string | null => {
  if (points.length < 2) return null;

  const first = points[0];
  const last = points[points.length - 1];
  const next = points[1];
  const prev = points[points.length - 2];

  const dxStart = next.x - first.x || 1;
  const dyStart = next.y - first.y;
  const lenStart = Math.hypot(dxStart, dyStart) || 1;
  const dxEnd = last.x - prev.x || 1;
  const dyEnd = last.y - prev.y;
  const lenEnd = Math.hypot(dxEnd, dyEnd) || 1;

  const startX = first.x + (dxStart / lenStart) * markerPad;
  const startY = first.y + (dyStart / lenStart) * markerPad;
  const endX = last.x - (dxEnd / lenEnd) * markerPad;
  const endY = last.y - (dyEnd / lenEnd) * markerPad;

  const isFlat = points.every(p => Math.abs(p.y - first.y) < 0.75);

  if (points.length === 2 && isFlat) {
    const midX = (startX + endX) / 2;
    const bumpY = startY - 7;
    return `M ${startX} ${startY} Q ${midX} ${bumpY} ${endX} ${endY}`;
  }

  if (points.length === 2) {
    return `M ${startX} ${startY} L ${endX} ${endY}`;
  }

  const inner = points
    .slice(1, -1)
    .map(p => `L ${p.x} ${p.y}`)
    .join(" ");
  return inner
    ? `M ${startX} ${startY} ${inner} L ${endX} ${endY}`
    : `M ${startX} ${startY} L ${endX} ${endY}`;
};

// Timeline Grid Component
const TimelineGrid: React.FC<TimelineGridProps> = ({
  accountName,
  timelineData,
  allDataPoints,
  formatDateFn,
  organizationLevels = [],
}) => {
  const sortedData = [...allDataPoints].sort(
    (a, b) =>
      a.timestamp - b.timestamp ||
      (a.assessmentName || "").localeCompare(b.assessmentName || "")
  );
  // One column per timeline point so same-day / same-level transitions still draw an arrow.
  const columnCount = Math.max(sortedData.length, 1);

  const levelsFromAPI = useMemo(() => {
    if (organizationLevels && organizationLevels.length > 0) {
      const levelNumbers = organizationLevels
        .map(level => {
          const match = level.level_code.match(/L?(\d+)/i);
          return match ? parseInt(match[1], 10) : null;
        })
        .filter((num): num is number => num !== null)
        .sort((a, b) => b - a);
      return levelNumbers;
    }
    return [7, 6, 5, 4, 3, 2, 1, 0];
  }, [organizationLevels]);

  const levels = levelsFromAPI;

  const getColumnPosition = (colIndex: number) => {
    if (columnCount <= 1) return 50;
    const columnWidth = 100 / columnCount;
    return colIndex * columnWidth + columnWidth / 2;
  };

  const fromDateCol = 0;
  const toDateCol = Math.max(columnCount - 1, 0);

  const getRowPosition = (level: number) => {
    const index = levels.indexOf(level);
    if (index === -1) {
      const minLevel = Math.min(...levels);
      const maxLevel = Math.max(...levels);
      const normalized = (level - minLevel) / (maxLevel - minLevel);
      return normalized * 100;
    }
    const rowHeight = 100 / levels.length;
    return index * rowHeight + rowHeight / 2;
  };

  const actualDataPoints = sortedData.map((point, dateCol) => ({
    x: getColumnPosition(dateCol),
    y: getRowPosition(point.level),
    level: point.level,
    publishedAt: point.publishedAt,
    dateCol,
  }));

  const baseOffset = columnCount > 0 ? 100 / columnCount : 100;
  const markerPad = Math.max(baseOffset * 0.08, 2.5);
  const arrowSlug = accountName.replace(/[^a-zA-Z0-9_-]/g, "-");

  return (
    <div className="w-full p-0 bg-white">
      <div className="relative flex flex-col gap-4 overflow-visible pt-10">
        <div className="flex flex-col border border-dashed border-gray-300 origin-center transition-transform duration-300 select-none relative ml-[3.75rem] mb-10">
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 flex items-center justify-center gap-1 z-30 bg-white px-3 py-1.5 rounded text-xs font-semibold">
            <span className="text-blue-500 font-bold">
              L{timelineData.from.level}
            </span>
            <span className="text-gray-900"> → </span>
            <span className="text-blue-500 font-bold">
              L{timelineData.to.level}
            </span>
          </div>
          <div className="absolute -left-[9.125rem] top-1/2 -translate-y-1/2 -rotate-90 text-sm font-semibold text-gray-900 whitespace-nowrap z-10">
            <span>Account Levels</span>
          </div>
          <div className="absolute -left-[3.75rem] top-0 flex flex-col h-full z-[5] w-[3.75rem]">
            {levels.map(level => (
              <div
                key={level}
                className="py-3.5 px-2 text-center text-sm font-medium text-gray-900 flex items-center justify-center flex-1 whitespace-nowrap"
              >
                L{level}
              </div>
            ))}
          </div>
          <div className="overflow-visible">
            {levels.map((level, levelIdx) => {
              const isLastRow = levelIdx === levels.length - 1;
              return (
                <div
                  key={level}
                  className="grid border-b border-dashed border-gray-300 min-h-16 last:border-b-0 overflow-visible"
                  style={{
                    gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
                  }}
                >
                  {sortedData.map((point, dateIdx) => {
                    const dataPointAtLevel =
                      point.level === level ? point : undefined;
                    const isFromPoint =
                      level === timelineData.from.level &&
                      dateIdx === fromDateCol;
                    const isToPoint =
                      level === timelineData.to.level && dateIdx === toDateCol;
                    const cellData = isFromPoint
                      ? timelineData.from
                      : isToPoint
                        ? timelineData.to
                        : dataPointAtLevel
                          ? {
                              level: point.level,
                              publishedAt: point.publishedAt,
                              assessmentName: point.assessmentName,
                            }
                          : undefined;
                    const cellType = isFromPoint
                      ? "from"
                      : isToPoint
                        ? "to"
                        : "main";
                    return (
                      <div
                        key={`${level}-${dateIdx}`}
                        className={`flex items-center justify-center p-1.5 px-1 border-r border-dashed border-gray-300 min-h-16 bg-white relative last:border-r-0 overflow-visible ${isToPoint ? "border-0" : ""}`}
                      >
                        {cellData && (
                          <CellContent
                            type={cellType}
                            accountName={accountName}
                            publishedAt={cellData.publishedAt}
                            assessmentName={cellData.assessmentName}
                            level={cellData.level}
                            formatDateFn={formatDateFn}
                            isLastRow={isLastRow}
                            columnIndex={dateIdx}
                            totalColumns={columnCount}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          <div
            className="absolute -bottom-8 right-0 grid gap-0 z-10 w-full"
            style={{
              gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
            }}
          >
            {sortedData.map((point, idx) => {
              const publishedAt = point.publishedAt;
              const formattedDate =
                formatAssessmentDateOrdinalUtc(publishedAt) ||
                formatDateFn(publishedAt);
              return (
                <div
                  key={`${publishedAt}-${idx}`}
                  className="p-1 px-0.5 text-center text-xs font-medium text-gray-900 border-r border-transparent leading-tight bg-white flex items-center justify-center w-full box-border"
                >
                  <span className="inline-block whitespace-nowrap overflow-visible text-center mx-auto">
                    {formattedDate}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="text-sm font-semibold text-gray-900 absolute left-1/2 -translate-x-1/2 -bottom-[3.75rem]">
            <span>Published Date</span>
          </div>
          <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-visible z-[25]">
            <svg
              className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-visible"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              <defs>
                <marker
                  id={`arrowhead-${arrowSlug}`}
                  markerWidth="8"
                  markerHeight="8"
                  refX="7"
                  refY="4"
                  orient="auto"
                  markerUnits="strokeWidth"
                  viewBox="0 0 8 8"
                >
                  <path d="M 0 0 L 8 4 L 0 8 Z" fill="#3b82f6" />
                </marker>
              </defs>
              {(() => {
                const pathData = buildTimelineArrowPath(
                  actualDataPoints,
                  markerPad
                );
                if (!pathData) return null;
                return (
                  <path
                    d={pathData}
                    stroke="#3b82f6"
                    strokeWidth="1.75"
                    strokeDasharray="4 3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                    fill="none"
                    markerEnd={`url(#arrowhead-${arrowSlug})`}
                  />
                );
              })()}
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Level colour palette ──────────────────────────────────────────────────────
const LEVEL_PIE_COLORS = [
  "#94a3b8",
  "#f87171",
  "#fb923c",
  "#fbbf24",
  "#60a5fa",
  "#4ade80",
];
const LEVEL_LABEL_BG = [
  "#f1f5f9",
  "#fee2e2",
  "#ffedd5",
  "#fef9c3",
  "#dbeafe",
  "#dcfce7",
];
const LEVEL_LABEL_TEXT = [
  "#1e293b",
  "#991b1b",
  "#9a3412",
  "#854d0e",
  "#1e40af",
  "#166534",
];

const getLevelNum = (level: string | number) => {
  const n = parseInt(String(level ?? "").replace(/[^0-9]/g, ""), 10);
  return isNaN(n) ? 0 : n;
};
const getLevelPieColor = (level: string | number) =>
  LEVEL_PIE_COLORS[getLevelNum(level) % LEVEL_PIE_COLORS.length];
const getLevelLabelBg = (level: string | number) =>
  LEVEL_LABEL_BG[getLevelNum(level) % LEVEL_LABEL_BG.length];
const getLevelLabelText = (level: string | number) =>
  LEVEL_LABEL_TEXT[getLevelNum(level) % LEVEL_LABEL_TEXT.length];

// Shimmer block
const ShimmerBlock: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div className={`animate-pulse rounded-md bg-gray-200 ${className}`} />
);

// ── Main Container ────────────────────────────────────────────────────────────
const LevelMixContainer: React.FC<LevelMixContainerProps> = ({
  orgId = null,
  defaultDateRange = null,
  showPortfolioFilter = false,
  allowedScopes,
  defaultScope = "formal",
  mixScopeLocked,
  initialMixScope,
  highlightAccountId = null,
  onMixScopeChange,
}) => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const user = loggedInUser?.user || loggedInUser;
  const executiveId = user?.executiveId || loggedInUser?.userId;

  const { levelMixError } = useAppSelector(state => state.assessment);
  const { organizationLevels } = useAppSelector(state => state.admin);

  const lockedScope = mixScopeLocked
    ? normalizeLevelMixScope(mixScopeLocked)
    : null;
  const resolvedAllowedScopes: LevelMixScope[] = lockedScope
    ? [lockedScope]
    : (allowedScopes ?? ["formal", "team", "my"]);
  const showScopeToggle = resolvedAllowedScopes.length > 1;
  /** My Assessments: Published/My primary + Formal/Team secondary. Hub stays Formal/Team. */
  const useNestedPublishedScopeUi =
    resolvedAllowedScopes.includes("my") &&
    resolvedAllowedScopes.includes("formal") &&
    resolvedAllowedScopes.includes("team");
  /** Formal convert from Incomplete Accounts is Hub-only (no `my` scope). */
  const showIncompleteFormalActions = !resolvedAllowedScopes.includes("my");

  const hasExplicitInitialMixScope = (() => {
    if (initialMixScope == null) return false;
    const raw = String(initialMixScope).trim().toLowerCase();
    if (!raw) return false;
    return ["formal", "team", "my", "org", "both", "self"].includes(raw);
  })();

  const pickInitialScope = (): LevelMixScope => {
    if (hasExplicitInitialMixScope) {
      const candidate = normalizeLevelMixScope(initialMixScope);
      if (resolvedAllowedScopes.includes(candidate)) return candidate;
    }
    if (useNestedPublishedScopeUi) {
      // Placeholder until published assessments resolve Formal vs Team.
      return "formal";
    }
    const candidate = normalizeLevelMixScope(
      initialMixScope ?? defaultScope ?? "formal"
    );
    if (resolvedAllowedScopes.includes(candidate)) return candidate;
    return resolvedAllowedScopes[0] ?? "formal";
  };

  const filterHubAssessmentsForScope = (
    assessments: any[],
    scope: LevelMixScope
  ): any[] => {
    if (scope === "team") {
      return assessments.filter(a => isPublishedSelfOrigin(a));
    }
    if (scope === "formal") {
      return assessments.filter(a => isPublishedFormalOrigin(a));
    }
    return assessments;
  };

  const mapHubAssessmentsToList = (assessments: any[]): Assessment[] =>
    sortPublishedNewestFirst(
      assessments
        .map((a: any) => {
          const effectiveDate =
            a.published_at || a.publishedAt || a.send_at || a.created_at;
          return {
            id: a.assessment_id,
            assessment_id: a.assessment_id,
            title: normalizeTeamAssessmentDisplayName(
              a.title || a.name,
              "Untitled Assessment"
            ),
            publishedAt: effectiveDate,
            published_at: effectiveDate,
            status: "published",
            notes: a.notes ?? null,
          };
        })
        .filter((a: Assessment) => Boolean(a.publishedAt || a.published_at))
    );

  const primaryScopeTabs = useMemo(
    () =>
      useNestedPublishedScopeUi
        ? (
            [
              {
                id: "published" as const,
                label: "Published",
                icon: <Archive size={14} strokeWidth={1.75} />,
              },
              {
                id: "my" as const,
                label: "My",
                icon: <UserRound size={14} strokeWidth={1.75} />,
              },
            ] as const
          ).filter(
            tab =>
              tab.id === "published" || resolvedAllowedScopes.includes("my")
          )
        : (
            [
              {
                id: "formal" as const,
                label: "Formal",
                icon: <BarChart3 size={14} strokeWidth={1.75} />,
              },
              {
                id: "team" as const,
                label: "Team",
                icon: <Users size={14} strokeWidth={1.75} />,
              },
              {
                id: "my" as const,
                label: "My",
                icon: <UserRound size={14} strokeWidth={1.75} />,
              },
            ] as const
          ).filter(tab => resolvedAllowedScopes.includes(tab.id)),
    [resolvedAllowedScopes, useNestedPublishedScopeUi]
  );

  const publishedKindTabs = useMemo(
    () =>
      (
        [
          {
            id: "formal" as const,
            label: "Formal",
            icon: <BarChart3 size={14} strokeWidth={1.75} />,
          },
          {
            id: "team" as const,
            label: "Team",
            icon: <Users size={14} strokeWidth={1.75} />,
          },
        ] as const
      ).filter(tab => resolvedAllowedScopes.includes(tab.id)),
    [resolvedAllowedScopes]
  );

  // ── Core data ──
  const [levelMixData, setLevelMixData] = useState<LevelMixItem[]>([]);
  const [incompleteAccountsData, setIncompleteAccountsData] = useState<any[]>(
    []
  );
  const [unassessedAccountIds, setUnassessedAccountIds] = useState<Set<string>>(
    () => new Set()
  );
  const [assessmentsList, setAssessmentsList] = useState<Assessment[]>([]);
  const [trendData, setTrendData] = useState<any[]>([]);

  // ── Date range ──
  const [dateRange, setDateRange] = useState(
    defaultDateRange || {
      startDate: utcMonthsAgoInputValue(1),
      endDate: utcTodayInputValue(),
    }
  );
  const [isDateWindowSettled, setIsDateWindowSettled] = useState(false);
  // Auto last-3 window vs user-picked From→To (full in-range list / incompletes).
  const [limitToLatestThree, setLimitToLatestThree] = useState(
    () => !defaultDateRange
  );

  // ── View mode ──
  const [viewMode, setViewMode] = useState<"consolidated" | "byAssessment">(
    "consolidated"
  );
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<
    string | null
  >(null);
  const [mixScope, setMixScope] = useState<LevelMixScope>(() =>
    pickInitialScope()
  );
  const [portfolioFilter, setPortfolioFilter] = useState<"all" | "myPortfolio">(
    "all"
  );
  /** After URL pin or user Formal/Team click; cleared when opening Published. */
  const publishedKindPinnedRef = useRef(
    hasExplicitInitialMixScope &&
      isPublishedOrgMixScope(normalizeLevelMixScope(initialMixScope))
  );

  useEffect(() => {
    const next = pickInitialScope();
    setMixScope(next);
    if (next === "my") setPortfolioFilter("all");
    publishedKindPinnedRef.current =
      hasExplicitInitialMixScope &&
      isPublishedOrgMixScope(normalizeLevelMixScope(initialMixScope));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMixScope, defaultScope]);

  useEffect(() => {
    if (!highlightAccountId || mixScope !== "my") return;
    const timer = window.setTimeout(() => {
      const el = document.getElementById(
        `levelmix-account-${highlightAccountId}`
      );
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [highlightAccountId, mixScope, levelMixData]);

  // ── Loading/ready flags ──
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const [levelMixLoading, setLevelMixLoading] = useState(false);
  const [trendLoading, setTrendLoading] = useState(false);
  const [isAssessmentsReady, setIsAssessmentsReady] = useState(false);
  const [isLevelMixReady, setIsLevelMixReady] = useState(false);
  const [isTrendReady, setIsTrendReady] = useState(false);
  const [showInitialShimmer, setShowInitialShimmer] = useState(true);

  // ── Summary ──
  const [summaryData, setSummaryData] = useState({
    assessmentCount: 0,
    accountCount: 0,
    totalTargeted: 0,
    sponsorCount: 0,
    unassessedCount: 0,
  });

  // ── UI state ──
  const [activePieSlice, setActivePieSlice] = useState<string | null>(null);
  const [incompleteOpen, setIncompleteOpen] = useState(false);
  const [incompleteSelectedKeys, setIncompleteSelectedKeys] = useState<
    Set<string>
  >(new Set());
  const [formalModalOpen, setFormalModalOpen] = useState(false);
  const [formalTargets, setFormalTargets] = useState<FormalRoundTarget[]>([]);
  const [formalName, setFormalName] = useState("");
  const [formalDescription, setFormalDescription] = useState("");
  const [formalDueOn, setFormalDueOn] = useState("");
  const [isFormalSubmitting, setIsFormalSubmitting] = useState(false);
  const [isCheckingFormalRunning, setIsCheckingFormalRunning] = useState(false);
  const [formalRunningConflict, setFormalRunningConflict] = useState<
    FormalRunningAccount[] | null
  >(null);
  const [trendAnimationTick, setTrendAnimationTick] = useState(0);

  // ── Modals ──
  const [showAccountsModal, setShowAccountsModal] = useState(false);
  const [modalAccountsData, setModalAccountsData] = useState<{
    displayItems: any[];
    levelItem: LevelMixItem;
    level: string | number;
    count: number;
    percentage: number | null;
  } | null>(null);
  const [showChartModal, setShowChartModal] = useState(false);
  const [chartData, setChartData] = useState<ChartDataPoint[] | null>(null);
  const [chartAccountName, setChartAccountName] = useState("");
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryAssessment, setSummaryAssessment] = useState<Assessment | null>(
    null
  );

  // ── Refs for caching ──
  const hasAutoExpanded = useRef(false);
  const prevPageShimmerLoadingRef = useRef(true);
  const allPublishedRef = useRef<Assessment[]>([]);
  const allPublishedRawRef = useRef<any[]>([]);
  const allPortfolioMixRef = useRef<Assessment[]>([]);
  const allMySubmitsRef = useRef<Assessment[]>([]);
  const allPublishedFullDataRef = useRef<any[]>([]);
  const hubFetchKeyRef = useRef("");
  const assessmentsListKeyRef = useRef("");
  const lastLevelMixKeyRef = useRef("");
  const lastTrendKeyRef = useRef("");
  const loadLevelMixRef = useRef<(() => Promise<void>) | null>(null);

  // ── Open Consolidated Report Modal using already-fetched published assessment data ──
  const openReportModal = (assessment: Assessment) => {
    const id = assessment.id || assessment.assessment_id;
    if (!id) return;
    const fullData = allPublishedFullDataRef.current.find(
      (a: any) => (a.assessment_id || a.id) === id
    );
    if (fullData) {
      const accounts = (fullData.target_accounts || []).map((acc: any) => ({
        id: acc.account_id,
        account_id: acc.account_id,
        name: acc.account_name,
        account_name: acc.account_name,
        account_code: acc.account_code,
        totalAssigned: acc.requests_sent,
        requests_sent: acc.requests_sent,
        responses: acc.responses_received,
        responses_received: acc.responses_received,
        finalLevel: acc.final_level || null,
        final_level: acc.final_level || null,
        medianLevel: acc.median_level || null,
        median_level: acc.median_level || null,
        override: acc.override,
        executives: (acc.users || []).map((u: any) => ({
          id: u.user_id || u.id,
          user_id: u.user_id || u.id,
          name: u.name,
          full_name: u.full_name,
          email: u.email,
          role: u.role,
        })),
      }));
      setSummaryAssessment({
        ...assessment,
        accounts,
        notes: fullData.notes ?? assessment.notes ?? null,
        published_at:
          fullData.published_at ||
          assessment.published_at ||
          assessment.publishedAt,
        created_at: fullData.created_at || (assessment as any).created_at,
        category: fullData.category || (assessment as any).category,
        created_by_user:
          fullData.created_by_user || (assessment as any).created_by_user,
        due_on:
          fullData.due_on || fullData.due_date || (assessment as any).due_on,
        name: fullData.name || fullData.title || assessment.title,
        title: fullData.title || fullData.name || assessment.title,
        description: fullData.description ?? (assessment as any).description,
      } as any);
    } else {
      setSummaryAssessment(assessment);
    }
    setShowSummaryModal(true);
  };

  const usesPortfolioMixWindow =
    isPublishedOrgMixScope(mixScope) &&
    portfolioFilter === "myPortfolio" &&
    Boolean(executiveId);

  const publishedWindowSource = (): Assessment[] =>
    usesPortfolioMixWindow
      ? allPortfolioMixRef.current
      : allPublishedRef.current;

  const resetMixCaches = () => {
    hasAutoExpanded.current = false;
    lastLevelMixKeyRef.current = "";
    lastTrendKeyRef.current = "";
    assessmentsListKeyRef.current = "";
    hubFetchKeyRef.current = "";
    allPublishedRef.current = [];
    allPublishedRawRef.current = [];
    allPortfolioMixRef.current = [];
    allPublishedFullDataRef.current = [];
    setLimitToLatestThree(true);
  };

  const applyPublishedScopeFilter = (scope: LevelMixScope) => {
    const scopedHub = filterHubAssessmentsForScope(
      allPublishedRawRef.current,
      scope
    );
    allPublishedRef.current = mapHubAssessmentsToList(scopedHub);
    allPublishedFullDataRef.current = scopedHub;
  };

  /** Soft reset when switching Formal ↔ Team so published list cache can be reused. */
  const resetMixCachesKeepPublished = () => {
    hasAutoExpanded.current = false;
    lastLevelMixKeyRef.current = "";
    lastTrendKeyRef.current = "";
    assessmentsListKeyRef.current = "";
    allPortfolioMixRef.current = [];
    setLimitToLatestThree(true);
  };

  const syncAssessmentsListFromMix = (
    levels: LevelMixItem[],
    options?: { myScopeOnly?: boolean; currentLevelOnly?: boolean },
    recentFromApi?: Assessment[]
  ) => {
    const derived = uniqueAssessmentsFromMix(levels, options);
    // Formal/Team: also include hub publishes in the active window so By Assessment
    // shows up to the last 3 published assessments, not only current mix rows.
    const fromHubWindow = isPublishedOrgMixScope(mixScope)
      ? publishedWindowSource()
      : [];
    const merged = mergeAssessmentsById(
      derived,
      recentFromApi || [],
      fromHubWindow
    );
    if (!merged.length) {
      assessmentsListKeyRef.current = "";
      setAssessmentsList([]);
      if (viewMode === "byAssessment") setSelectedAssessmentId(null);
      return;
    }

    const startBound = parseUtcDateBound(dateRange.startDate);
    const endBound = parseUtcDateBound(dateRange.endDate, true);
    const inRange = merged.filter(a => {
      const d = parseAssessmentDate(a.publishedAt || a.published_at || "");
      return d != null && d >= startBound && d <= endBound;
    });
    let list = sortPublishedNewestFirst(inRange);
    // Default Formal/Team UX: newest 3. Manual date range: every publish in From→To
    // (By Assessment dropdown, incompletes, and Recent follow this list).
    if (isPublishedOrgMixScope(mixScope) && limitToLatestThree) {
      list = list.slice(0, 3);
    }
    const nextKey = list.map(a => a.id).join("|");
    if (assessmentsListKeyRef.current !== nextKey) {
      assessmentsListKeyRef.current = nextKey;
      setAssessmentsList(list);
    }

    if (
      viewMode === "byAssessment" &&
      list.length > 0 &&
      !selectedAssessmentId
    ) {
      setSelectedAssessmentId(list[0].id);
    } else if (viewMode === "byAssessment" && list.length === 0) {
      setSelectedAssessmentId(null);
    } else if (
      viewMode === "byAssessment" &&
      selectedAssessmentId &&
      !list.some(a => a.id === selectedAssessmentId)
    ) {
      setSelectedAssessmentId(list[0]?.id ?? null);
    }
  };

  const maybeAutoExpandPublishedWindow = (source: Assessment[]): boolean => {
    if (
      !isPublishedOrgMixScope(mixScope) ||
      hasAutoExpanded.current ||
      defaultDateRange ||
      source.length === 0
    ) {
      return false;
    }

    hasAutoExpanded.current = true;
    const targetRange = dateRangeSpanningLastPublished(source, 3);
    const currentStart = dateRange.startDate
      ? toDateInputValue(dateRange.startDate)
      : null;
    const currentEnd = dateRange.endDate
      ? toDateInputValue(dateRange.endDate)
      : null;

    if (
      targetRange &&
      (currentStart !== targetRange.startDate ||
        currentEnd !== targetRange.endDate)
    ) {
      setIsDateWindowSettled(false);
      setDateRange(targetRange);
      return true;
    }
    return false;
  };

  // ── Auto-expand date window for published org / my scopes ──
  const loadAssessmentsList = async () => {
    if (mixScope === "my") {
      setAssessmentsLoading(false);
      setIsAssessmentsReady(true);
      setIsDateWindowSettled(true);
      return;
    }

    let didAdjustDateWindow = false;
    let deferForScopeResolve = false;
    try {
      setAssessmentsLoading(true);

      const fetchKey = JSON.stringify({
        orgId: orgId || "",
        portfolioFilter,
        executiveId: usesPortfolioMixWindow ? executiveId || "" : "",
        // Portfolio window is scope-specific; hub published list is shared.
        mixScope: usesPortfolioMixWindow ? mixScope : "published",
      });

      if (usesPortfolioMixWindow) {
        if (
          hubFetchKeyRef.current !== fetchKey ||
          allPortfolioMixRef.current.length === 0
        ) {
          hubFetchKeyRef.current = fetchKey;
          const today = utcTodayInputValue();
          const wideResult = await dispatch(
            fetchLevelMix({
              startDate: "2000-01-01",
              endDate: today,
              scope: mixScope,
              executiveId: String(executiveId),
            })
          );
          if (fetchLevelMix.fulfilled.match(wideResult)) {
            allPortfolioMixRef.current = mergeAssessmentsById(
              uniqueAssessmentsFromMix(wideResult.payload?.levels, {
                currentLevelOnly: true,
              }),
              normalizeRecentAssessments(wideResult.payload?.recentAssessments)
            );
          } else {
            allPortfolioMixRef.current = [];
          }
        }
      } else if (
        hubFetchKeyRef.current !== fetchKey ||
        allPublishedRawRef.current.length === 0
      ) {
        hubFetchKeyRef.current = fetchKey;
        try {
          const result = await dispatch(
            fetchAssessments({
              status: "published",
              sort_by: "created_at",
              sort_order: "desc",
            })
          );
          if (fetchAssessments.fulfilled.match(result)) {
            allPublishedRawRef.current = result.payload?.assessments || [];
          } else {
            allPublishedRawRef.current = [];
            allPublishedRef.current = [];
            allPublishedFullDataRef.current = [];
          }
        } catch {
          hubFetchKeyRef.current = "";
          throw new Error("Failed to fetch assessments");
        }
      }

      if (
        useNestedPublishedScopeUi &&
        !publishedKindPinnedRef.current &&
        !usesPortfolioMixWindow
      ) {
        const picked = pickPublishedLevelMixScope(allPublishedRawRef.current);
        publishedKindPinnedRef.current = true;
        if (picked !== mixScope) {
          applyPublishedScopeFilter(picked);
          deferForScopeResolve = true;
          setMixScope(picked);
          onMixScopeChange?.(picked);
          return;
        }
      }

      if (!usesPortfolioMixWindow) {
        applyPublishedScopeFilter(mixScope);
      }

      const windowSource = publishedWindowSource();
      if (maybeAutoExpandPublishedWindow(windowSource)) {
        didAdjustDateWindow = true;
        return;
      }
    } catch (error) {
      console.error("Error loading assessments list:", error);
      assessmentsListKeyRef.current = "";
      setAssessmentsList([]);
      if (viewMode === "byAssessment") setSelectedAssessmentId(null);
    } finally {
      setAssessmentsLoading(false);
      if (!deferForScopeResolve) {
        setIsAssessmentsReady(true);
        setIsDateWindowSettled(!didAdjustDateWindow);
      }
    }
  };

  // ── Load level mix ──
  const loadLevelMix = async () => {
    try {
      setLevelMixLoading(true);

      const startDateFormatted = dateRange.startDate
        ? toDateInputValue(dateRange.startDate)
        : undefined;
      const endDateFormatted = dateRange.endDate
        ? toDateInputValue(dateRange.endDate)
        : undefined;

      const params: {
        startDate?: string;
        endDate?: string;
        assessmentId?: string;
        executiveId?: string;
        scope?: LevelMixScope;
      } = {
        startDate: startDateFormatted,
        endDate: endDateFormatted,
        scope: mixScope,
      };

      if (viewMode === "byAssessment" && selectedAssessmentId) {
        params.assessmentId = selectedAssessmentId;
      }
      if (mixScope === "my" && executiveId) {
        params.executiveId = String(executiveId);
      } else if (
        isPublishedOrgMixScope(mixScope) &&
        portfolioFilter === "myPortfolio" &&
        executiveId
      ) {
        params.executiveId = String(executiveId);
      }

      if (mixScope === "my" && !hasAutoExpanded.current && !defaultDateRange) {
        hasAutoExpanded.current = true;
        const today = utcTodayInputValue();
        const wideResult = await dispatch(
          fetchLevelMix({
            startDate: "2000-01-01",
            endDate: today,
            scope: "my",
            ...(executiveId ? { executiveId: String(executiveId) } : {}),
          })
        );
        if (fetchLevelMix.fulfilled.match(wideResult)) {
          const derived = uniqueAssessmentsFromMix(wideResult.payload?.levels, {
            myScopeOnly: true,
          });
          allMySubmitsRef.current = derived;
          const spanned = dateRangeSpanningLastPublished(derived, 3);
          const currentStart = startDateFormatted || null;
          const currentEnd = endDateFormatted || null;
          if (
            spanned &&
            (currentStart !== spanned.startDate ||
              currentEnd !== spanned.endDate)
          ) {
            lastLevelMixKeyRef.current = "";
            setIsDateWindowSettled(false);
            setDateRange(spanned);
            setLevelMixLoading(false);
            setIsLevelMixReady(true);
            return;
          }
        }
      }

      const levelMixKey = JSON.stringify(params);
      if (lastLevelMixKeyRef.current === levelMixKey) {
        setLevelMixLoading(false);
        setIsLevelMixReady(true);
        return;
      }
      lastLevelMixKeyRef.current = levelMixKey;

      const result = await dispatch(fetchLevelMix(params));
      if (fetchLevelMix.fulfilled.match(result)) {
        const response = result.payload;
        const levels = normalizeLevelMixLevels(response?.levels || []);
        const totalTargeted =
          response?.totalTargeted ?? response?.totalAccounts ?? 0;
        const sponsorCount = response?.sponsorCount ?? 0;
        const incompleteAccounts =
          mixScope === "my" ? [] : (response?.incompleteAccounts ?? []);
        const unassessedRows =
          mixScope === "my" ? [] : (response?.unassessedAccounts ?? []);
        const unassessedIdSet = new Set<string>();
        for (const row of unassessedRows) {
          if (row?.id != null) unassessedIdSet.add(String(row.id));
        }
        const unassessedCount =
          mixScope === "my"
            ? 0
            : (response?.unassessedCount ?? unassessedIdSet.size);

        setLevelMixData(levels);
        setIncompleteAccountsData(incompleteAccounts);
        setUnassessedAccountIds(unassessedIdSet);

        const recentFromApi = normalizeRecentAssessments(
          response?.recentAssessments
        );

        if (mixScope === "my") {
          const derived = uniqueAssessmentsFromMix(levels, {
            myScopeOnly: true,
          });
          if (derived.length) {
            allMySubmitsRef.current = sortPublishedNewestFirst([
              ...allMySubmitsRef.current.filter(
                a => !derived.some(d => d.id === a.id)
              ),
              ...derived,
            ]);
          }
          syncAssessmentsListFromMix(levels, { myScopeOnly: true });
        } else if (isPublishedOrgMixScope(mixScope)) {
          if (recentFromApi.length) {
            allPortfolioMixRef.current = mergeAssessmentsById(
              allPortfolioMixRef.current,
              recentFromApi
            );
          }
          syncAssessmentsListFromMix(
            levels,
            { currentLevelOnly: true },
            recentFromApi
          );
        }

        // Count unique accounts/assessments
        const uniqueAccountIds = new Set<string>();
        const uniqueAssessmentIds = new Set<string>();
        levels.forEach((levelItem: LevelMixItem) => {
          levelItem.accounts?.forEach(acc => {
            uniqueAccountIds.add(acc.id);
            if (acc.assessmentId) uniqueAssessmentIds.add(acc.assessmentId);
          });
        });
        recentFromApi.forEach(a => uniqueAssessmentIds.add(a.id));

        const assessmentCount =
          viewMode === "byAssessment" && selectedAssessmentId
            ? 1
            : response?.totalAssessments || uniqueAssessmentIds.size;
        const accountCount = response?.totalAccounts ?? uniqueAccountIds.size;

        setSummaryData({
          assessmentCount,
          accountCount,
          totalTargeted: totalTargeted ?? accountCount,
          sponsorCount,
          unassessedCount,
        });
      } else {
        setLevelMixData([]);
        setIncompleteAccountsData([]);
        setUnassessedAccountIds(new Set());
        syncAssessmentsListFromMix([]);
        setSummaryData({
          assessmentCount: 0,
          accountCount: 0,
          totalTargeted: 0,
          sponsorCount: 0,
          unassessedCount: 0,
        });
        if (levelMixError) toast.error(levelMixError);
      }
    } catch (error) {
      console.error("Error loading level mix:", error);
      toast.error("Failed to load level mix data");
      setLevelMixData([]);
      setIncompleteAccountsData([]);
      setUnassessedAccountIds(new Set());
      syncAssessmentsListFromMix([]);
      setSummaryData({
        assessmentCount: 0,
        accountCount: 0,
        totalTargeted: 0,
        sponsorCount: 0,
        unassessedCount: 0,
      });
    } finally {
      setLevelMixLoading(false);
      setIsLevelMixReady(true);
    }
  };
  loadLevelMixRef.current = loadLevelMix;

  // ── Load trend data (top 3 assessments, oldest → newest) ──
  const loadTrendData = async (assessments: Assessment[]) => {
    if (!assessments || assessments.length === 0) {
      setTrendData([]);
      setIsTrendReady(true);
      return;
    }
    try {
      setTrendLoading(true);
      const top3 = assessments.slice(0, 3);
      const trendKey = JSON.stringify({
        viewMode,
        ids: top3.map(a => a.id),
        dates: top3.map(a =>
          toDateInputValue(a.publishedAt || a.published_at || "")
        ),
        orgId: orgId || "",
        mixScope,
        portfolioFilter: isPublishedOrgMixScope(mixScope)
          ? portfolioFilter
          : "all",
        executiveId:
          mixScope === "my" ||
          (isPublishedOrgMixScope(mixScope) &&
            portfolioFilter === "myPortfolio")
            ? executiveId || ""
            : "",
      });
      if (lastTrendKeyRef.current === trendKey) {
        setTrendLoading(false);
        setIsTrendReady(true);
        return;
      }
      lastTrendKeyRef.current = trendKey;

      const results = await Promise.all(
        top3.map(async assessment => {
          try {
            const params: Record<string, string> = {
              assessmentId: assessment.id,
              startDate: toDateInputValue(dateRange.startDate) || "",
              endDate: toDateInputValue(dateRange.endDate) || "",
              scope: mixScope,
            };
            if (!params.startDate || !params.endDate) {
              return {
                assessment,
                levelDistribution: [],
                totalTargeted: null,
                incompleteAccounts: [],
              };
            }
            if (mixScope === "my" && executiveId) {
              params.executiveId = String(executiveId);
            } else if (
              isPublishedOrgMixScope(mixScope) &&
              portfolioFilter === "myPortfolio" &&
              executiveId
            ) {
              params.executiveId = String(executiveId);
            }
            const res = (await dispatch(
              fetchLevelMixSnapshot(params)
            ).unwrap()) as any;
            const raw = res;
            const levels = Array.isArray(raw) ? raw : raw?.levels || [];
            const totalTargeted = Array.isArray(raw)
              ? null
              : (raw?.totalTargeted ?? null);
            const incompleteAccounts = Array.isArray(raw)
              ? []
              : (raw?.incompleteAccounts ?? []);
            return {
              assessment,
              levelDistribution: levelMixRowsForPie(
                normalizeLevelMixLevels(levels)
              ),
              totalTargeted,
              incompleteAccounts,
            };
          } catch {
            return {
              assessment,
              levelDistribution: [],
              totalTargeted: null,
              incompleteAccounts: [],
            };
          }
        })
      );
      results.sort(
        (a, b) =>
          new Date(
            a.assessment.publishedAt || a.assessment.published_at || 0
          ).getTime() -
          new Date(
            b.assessment.publishedAt || b.assessment.published_at || 0
          ).getTime()
      );
      setTrendData(results);
    } catch (err) {
      console.error("Error loading trend data:", err);
      setTrendData([]);
    } finally {
      setTrendLoading(false);
      setIsTrendReady(true);
    }
  };

  // ── Effects ──
  useEffect(() => {
    if (organizationLevels.length === 0) dispatch(fetchOrganizationLevels());
  }, [dispatch, organizationLevels.length]);

  useEffect(() => {
    loadAssessmentsList();
  }, [
    dateRange.startDate,
    dateRange.endDate,
    orgId,
    mixScope,
    portfolioFilter,
    executiveId,
  ]);

  useEffect(() => {
    if (!isDateWindowSettled) return;
    loadLevelMix();
  }, [
    dateRange.startDate,
    dateRange.endDate,
    orgId,
    viewMode,
    selectedAssessmentId,
    mixScope,
    portfolioFilter,
    executiveId,
    isDateWindowSettled,
  ]);

  useEffect(() => {
    if (!isAssessmentsReady || !isDateWindowSettled) return;

    if (viewMode === "byAssessment") {
      if (!selectedAssessmentId) {
        setTrendData([]);
        setIsTrendReady(true);
        lastTrendKeyRef.current = "";
        return;
      }
      const selected = assessmentsList.find(a => a.id === selectedAssessmentId);
      if (!selected) {
        setTrendData([]);
        setIsTrendReady(true);
        lastTrendKeyRef.current = "";
        return;
      }
      setIsTrendReady(false);
      loadTrendData([selected]);
      return;
    }

    if (assessmentsList.length > 0) setIsTrendReady(false);
    loadTrendData(assessmentsList);
  }, [
    assessmentsList,
    isAssessmentsReady,
    isDateWindowSettled,
    viewMode,
    selectedAssessmentId,
    dateRange.startDate,
    dateRange.endDate,
    mixScope,
    portfolioFilter,
    executiveId,
  ]);

  // Turn off full-page shimmer after first complete load
  useEffect(() => {
    if (
      showInitialShimmer &&
      isAssessmentsReady &&
      isLevelMixReady &&
      isTrendReady &&
      !assessmentsLoading &&
      !levelMixLoading &&
      !trendLoading
    ) {
      setShowInitialShimmer(false);
    }
  }, [
    showInitialShimmer,
    isAssessmentsReady,
    isLevelMixReady,
    isTrendReady,
    assessmentsLoading,
    levelMixLoading,
    trendLoading,
  ]);

  const detailedIncompleteAccounts = useMemo(() => {
    const byKey = new Map<string, any>();

    // Formal/Team auto last-3: only incompletes for assessmentsList.
    // Manual date range: trust API window (complete → levels, incomplete → here).
    // By Assessment: always scope to the selected assessment.
    const allowedAssessmentIds = (() => {
      if (!isPublishedOrgMixScope(mixScope)) return null;
      if (viewMode === "byAssessment" && selectedAssessmentId) {
        return new Set([String(selectedAssessmentId)]);
      }
      if (!limitToLatestThree) return null;
      if (assessmentsList.length > 0) {
        return new Set(assessmentsList.map(a => String(a.id)));
      }
      // Auto mode list not ready — avoid flashing older-window incompletes.
      return new Set<string>();
    })();

    const resolveAssessmentName = (
      ...candidates: Array<string | null | undefined>
    ) => {
      for (const candidate of candidates) {
        const normalized = normalizeTeamAssessmentDisplayName(candidate, "");
        if (normalized.trim()) return normalized;
      }
      return "";
    };

    const accountAssessmentId = (account: any, fallback?: string) =>
      String(account?.assessmentId || account?.assessment_id || fallback || "");

    const rowKey = (accountId: string, assessmentId: string) =>
      `${accountId}::${assessmentId || "_"}`;

    const addAccount = (
      account: any,
      assessmentName?: string,
      fallbackAssessmentId?: string
    ) => {
      if (!account?.id) return;
      const aid = accountAssessmentId(account, fallbackAssessmentId);
      if (allowedAssessmentIds && (!aid || !allowedAssessmentIds.has(aid))) {
        return;
      }
      const nextName = resolveAssessmentName(
        assessmentName,
        account.assessmentName,
        account.assessment_name
      );
      const next = {
        ...account,
        ...(aid ? { assessmentId: aid } : {}),
        ...(nextName
          ? { assessmentName: nextName }
          : { assessmentName: undefined }),
      };
      const key = rowKey(String(account.id), aid);
      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, next);
        return;
      }

      const nextRatio =
        next.totalAssigned > 0 ? next.respondedCount / next.totalAssigned : 1;
      const existingRatio =
        existing.totalAssigned > 0
          ? existing.respondedCount / existing.totalAssigned
          : 1;
      const preferNext = nextRatio < existingRatio;
      const merged = preferNext
        ? { ...existing, ...next }
        : { ...next, ...existing };
      // Keep a real assessment title whenever either side has one.
      merged.assessmentName =
        resolveAssessmentName(
          preferNext ? next.assessmentName : existing.assessmentName,
          preferNext ? existing.assessmentName : next.assessmentName
        ) || undefined;
      byKey.set(key, merged);
    };

    normalizeIncompleteAccounts(incompleteAccountsData).forEach(account =>
      addAccount(account)
    );

    trendData.forEach(slot => {
      const assessmentName = slot?.assessment?.title;
      const slotAssessmentId = String(
        slot?.assessment?.id || slot?.assessment?.assessment_id || ""
      );
      normalizeIncompleteAccounts(slot?.incompleteAccounts).forEach(account =>
        addAccount(account, assessmentName, slotAssessmentId)
      );
    });

    return Array.from(byKey.values());
  }, [
    incompleteAccountsData,
    trendData,
    mixScope,
    viewMode,
    selectedAssessmentId,
    assessmentsList,
    limitToLatestThree,
  ]);

  const incompleteRowKey = (account: any) =>
    `${String(account?.id || "")}::${String(account?.assessmentId || account?.assessment_id || "")}`;

  useEffect(() => {
    setIncompleteSelectedKeys(new Set());
  }, [detailedIncompleteAccounts]);

  const disconnectFlowValue = useAppSelector(
    s => s.vendorAdmin.disconnectFlowValue
  );

  useEffect(() => {
    dispatch(fetchDisconnectFlowConfig());
  }, [dispatch]);

  const incompleteAccountNamesById = useMemo(() => {
    const map: Record<string, string> = {};
    detailedIncompleteAccounts.forEach((a: any) => {
      if (a?.id) map[String(a.id)] = a.name || "Unknown account";
    });
    return map;
  }, [detailedIncompleteAccounts]);

  /** Account ids also listed under Incomplete Accounts (mark with ⚠ in distribution). */
  const incompleteAccountIds = useMemo(() => {
    const ids = new Set<string>();
    detailedIncompleteAccounts.forEach((a: any) => {
      if (a?.id) ids.add(String(a.id));
    });
    return ids;
  }, [detailedIncompleteAccounts]);

  const closeFormalModal = useCallback(() => {
    setFormalModalOpen(false);
    setFormalTargets([]);
    setFormalName("");
    setFormalDescription("");
    setFormalDueOn("");
  }, []);

  const stakeholderIdsFromTeamsBatch = (
    accountId: string,
    batch: Record<string, any>
  ): string[] => {
    const entry = batch[accountId];
    if (!entry) return [];
    const ids: string[] = [];
    const account = entry.account ?? {};
    const primary =
      account.primaryExecutive?.user_id ||
      account.primaryExecutive?.id ||
      account.primary_executive_id;
    const secondary =
      account.secondaryExecutive?.user_id ||
      account.secondaryExecutive?.id ||
      account.secondary_executive_id;
    if (primary) ids.push(String(primary));
    if (secondary) ids.push(String(secondary));
    for (const member of entry.accountTeam ?? []) {
      if (member?.id) ids.push(String(member.id));
    }
    for (const sponsor of entry.sponsors ?? []) {
      if (sponsor?.executive_id) ids.push(String(sponsor.executive_id));
    }
    return Array.from(new Set(ids.filter(Boolean)));
  };

  const handleIncompleteFormalClick = useCallback(async () => {
    const selectedRows = detailedIncompleteAccounts.filter((a: any) =>
      incompleteSelectedKeys.has(incompleteRowKey(a))
    );
    if (!selectedRows.length) {
      toast.error("Select at least one incomplete account to start formal.");
      return;
    }

    const accountIds = Array.from(
      new Set(selectedRows.map((a: any) => String(a.id || "")).filter(Boolean))
    );

    setIsCheckingFormalRunning(true);
    try {
      const blocking = await dispatch(
        checkFormalRunning({ account_ids: accountIds })
      ).unwrap();
      if (blocking.length) {
        setFormalRunningConflict(blocking);
        return;
      }

      const byAccount = new Map<string, FormalRoundTarget>();
      for (const row of selectedRows) {
        const accountId = String(row.id || "");
        if (!accountId || byAccount.has(accountId)) continue;
        byAccount.set(accountId, {
          assessment_id: accountId,
          account_id: accountId,
          account_name: row.name || "Unknown account",
          name: row.name || "Unknown account",
          title: row.assessmentName || undefined,
          source_assessment_id:
            row.assessmentId || row.assessment_id || undefined,
        });
      }
      const targets = Array.from(byAccount.values());
      if (!targets.length) {
        toast.error("Select at least one incomplete account to start formal.");
        return;
      }

      setFormalTargets(targets);
      setFormalName(buildDefaultFormalName(targets));
      setFormalDescription(buildDefaultFormalDescription(targets.length));
      setFormalDueOn(
        buildDefaultFormalDueOn(disconnectFlowValue?.formal_default_due_date)
      );
      setFormalModalOpen(true);
    } catch (e: unknown) {
      toast.error(
        typeof e === "string"
          ? e
          : e instanceof Error
            ? e.message
            : "Failed to prepare formal round."
      );
    } finally {
      setIsCheckingFormalRunning(false);
    }
  }, [
    detailedIncompleteAccounts,
    incompleteSelectedKeys,
    dispatch,
    disconnectFlowValue?.formal_default_due_date,
    toast,
  ]);

  const handleConfirmFormal = useCallback(async () => {
    if (!formalTargets.length) return;
    const name = formalName.trim();
    const description = formalDescription.trim();
    const due_on = formalDueOn.trim();
    if (!name) {
      toast.error("Assessment name is required.");
      return;
    }
    if (!due_on) {
      toast.error("Due date is required.");
      return;
    }

    const accountIds = formalTargets
      .map(t => t.account_id || t.assessment_id)
      .filter(Boolean);
    const sourceAssessmentId = formalTargets.find(
      t => t.source_assessment_id
    )?.source_assessment_id;

    if (!sourceAssessmentId) {
      toast.error(
        "Cannot start formal: missing source assessment for questions."
      );
      return;
    }

    setIsFormalSubmitting(true);
    try {
      const [questionsPayload, teamsBatch] = await Promise.all([
        dispatch(
          fetchAssessmentQuestionsAndResponses(sourceAssessmentId)
        ).unwrap(),
        dispatch(fetchAssessmentAccountsTeamsBatch({ accountIds })).unwrap(),
      ]);

      const qrRoot =
        (questionsPayload as any)?.content ?? questionsPayload ?? {};
      const questionsBlock = qrRoot?.questions;
      const questionsRaw = Array.isArray(questionsBlock)
        ? questionsBlock
        : questionsBlock?.questions;
      if (!Array.isArray(questionsRaw) || questionsRaw.length === 0) {
        throw new Error("Source assessment has no questions to copy.");
      }
      const sourceMetadata =
        !Array.isArray(questionsBlock) &&
        questionsBlock &&
        typeof questionsBlock === "object"
          ? questionsBlock.metadata || {}
          : {};

      const accountsPayload = accountIds.map(accountId => {
        const executiveIds = stakeholderIdsFromTeamsBatch(
          accountId,
          teamsBatch || {}
        );
        return { account_id: accountId, executive_ids: executiveIds };
      });
      const missingExecs = accountsPayload.filter(a => !a.executive_ids.length);
      if (missingExecs.length) {
        const names = missingExecs
          .map(a => incompleteAccountNamesById[a.account_id] || a.account_id)
          .join(", ");
        throw new Error(
          `No stakeholders configured for: ${names}. Assign sponsors or account team first.`
        );
      }

      const templateId =
        sourceMetadata?.template_id || qrRoot?.template_id || undefined;
      const category =
        (Array.isArray(sourceMetadata?.categories) &&
          sourceMetadata.categories[0]) ||
        "short";

      await dispatch(
        createAssessment({
          name,
          description:
            description || buildDefaultFormalDescription(formalTargets.length),
          accounts: accountsPayload,
          data: {
            metadata: {
              categories: [],
              ...(templateId ? { template_id: templateId } : {}),
            },
            questions: questionsRaw,
          },
          target_questionnaires: category,
          status: "active",
          due_on,
          reminder_frequency: "Daily",
          send_now: true,
          ...(templateId ? { template_id: templateId } : {}),
        } as any)
      ).unwrap();

      toast.success("Formal assessment round started.");
      setIncompleteSelectedKeys(new Set());
      closeFormalModal();
      lastLevelMixKeyRef.current = "";
      void loadLevelMixRef.current?.();
    } catch (e: unknown) {
      const rejection = e as {
        message?: string;
        code?: string;
        accounts?: FormalRunningAccount[];
      };
      if (rejection?.code === "FORMAL_RUNNING" && rejection.accounts?.length) {
        closeFormalModal();
        setFormalRunningConflict(rejection.accounts);
      } else {
        toast.error(
          typeof e === "string"
            ? e
            : rejection?.message ||
                (e instanceof Error
                  ? e.message
                  : "Failed to start formal round.")
        );
      }
    } finally {
      setIsFormalSubmitting(false);
    }
  }, [
    formalTargets,
    formalName,
    formalDescription,
    formalDueOn,
    dispatch,
    toast,
    closeFormalModal,
    incompleteAccountNamesById,
  ]);

  const incompleteAccountCount = useMemo(() => {
    // Formal/Team: list length (includes Unassessed rows for Incomplete Accounts UI).
    if (isPublishedOrgMixScope(mixScope)) {
      return detailedIncompleteAccounts.length;
    }
    const gapCount = Math.max(
      0,
      (summaryData.totalTargeted || summaryData.accountCount) -
        summaryData.accountCount
    );
    return detailedIncompleteAccounts.length > 0
      ? detailedIncompleteAccounts.length
      : gapCount;
  }, [detailedIncompleteAccounts, summaryData, mixScope]);

  // Incomplete KPI excludes Unassessed (Unknown / no real AL) and counts unique
  // accounts so duplicates across assessments do not inflate the card.
  const incompleteKpiCount = useMemo(() => {
    if (!isPublishedOrgMixScope(mixScope)) {
      return incompleteAccountCount;
    }
    const ids = new Set<string>();
    for (const row of detailedIncompleteAccounts) {
      const id = row?.id != null ? String(row.id) : "";
      if (!id || unassessedAccountIds.has(id)) continue;
      ids.add(id);
    }
    return ids.size;
  }, [
    mixScope,
    incompleteAccountCount,
    detailedIncompleteAccounts,
    unassessedAccountIds,
  ]);

  const hasIncompleteAccounts = mixScope !== "my" && incompleteAccountCount > 0;

  const hasLevelMixAccounts = useMemo(
    () => levelMixData.some(levelItem => (levelItem.accounts?.length ?? 0) > 0),
    [levelMixData]
  );

  const chartTrendData = useMemo(() => {
    if (trendData.length > 0) return trendData;

    if (
      viewMode === "byAssessment" &&
      selectedAssessmentId &&
      levelMixData.length > 0
    ) {
      const selected =
        assessmentsList.find(a => a.id === selectedAssessmentId) ||
        allPublishedRef.current.find(a => a.id === selectedAssessmentId);
      if (!selected) return [];

      return [
        {
          assessment: selected,
          levelDistribution: levelMixRowsForPie(levelMixData),
          totalTargeted: summaryData.totalTargeted,
          incompleteAccounts: incompleteAccountsData,
        },
      ];
    }

    return [];
  }, [
    trendData,
    viewMode,
    selectedAssessmentId,
    levelMixData,
    assessmentsList,
    summaryData.totalTargeted,
    incompleteAccountsData,
  ]);

  useEffect(() => {
    if (hasIncompleteAccounts) {
      setIncompleteOpen(true);
    }
  }, [
    hasIncompleteAccounts,
    dateRange.startDate,
    dateRange.endDate,
    viewMode,
    selectedAssessmentId,
  ]);

  // Replay pie animation on shimmer → content transition
  const isPageShimmerLoading =
    showInitialShimmer &&
    (assessmentsLoading ||
      levelMixLoading ||
      trendLoading ||
      !isAssessmentsReady ||
      !isLevelMixReady ||
      !isTrendReady);

  useEffect(() => {
    if (prevPageShimmerLoadingRef.current && !isPageShimmerLoading) {
      setTrendAnimationTick(t => t + 1);
    }
    prevPageShimmerLoadingRef.current = isPageShimmerLoading;
  }, [isPageShimmerLoading]);

  // ── Helpers ──
  const formatDate = (dateString: string): string => {
    if (!dateString) return "";
    return formatAssessmentDateTimeUtc(dateString, true) || "";
  };

  const formatDateTime = (dateString: string): string => {
    if (!dateString) return "";
    return formatAssessmentDateTimeUtc(dateString) || "";
  };

  const alPublishedAtFor = (
    assessmentId?: string | null,
    fallback?: string | null
  ): string => {
    if (!assessmentId) return fallback || "";
    const fromList =
      assessmentsList.find(a => a.id === assessmentId) ||
      allPublishedRef.current.find(a => a.id === assessmentId);
    return fromList?.publishedAt || fromList?.published_at || fallback || "";
  };

  const handleAccountClick = (displayItem: any) => {
    const allAccountData: any[] = [];
    levelMixData.forEach(levelItem => {
      levelItem.accounts?.forEach(acc => {
        if (acc.name !== displayItem.accountName) return;
        const rawPublishedAt =
          mixScope === "my"
            ? (((acc as any).youSubmittedAt ||
                (acc as any).orgPublishedAt ||
                acc.publishedAt ||
                "") as string)
            : alPublishedAtFor(
                acc.assessmentId ?? undefined,
                acc.publishedAt ?? undefined
              );
        if (!rawPublishedAt) return;
        const parsed = new Date(rawPublishedAt);
        if (!Number.isFinite(parsed.getTime())) return;
        allAccountData.push({
          level: parseLevelNumber(levelItem.level),
          publishedAt: rawPublishedAt,
          date: parsed,
          assessmentId: acc.assessmentId,
          assessmentName: normalizeTeamAssessmentDisplayName(
            acc.assessmentName,
            "Untitled Assessment"
          ),
        });
      });
    });

    const uniqueData: any[] = [];
    const seen = new Set<string>();
    allAccountData.forEach(item => {
      const key = `${item.assessmentId}_${item.date.getTime()}_${item.level}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniqueData.push(item);
      }
    });
    uniqueData.sort(
      (a, b) =>
        a.date.getTime() - b.date.getTime() ||
        (a.assessmentName || "").localeCompare(b.assessmentName || "")
    );

    const chartDataArray = uniqueData.map(occ => ({
      date: formatDateTime(occ.publishedAt),
      dateOnly: formatDate(occ.publishedAt),
      level: occ.level,
      timestamp: occ.date.getTime(),
      assessmentName: occ.assessmentName,
      publishedAt: occ.publishedAt,
    }));

    setChartData(chartDataArray);
    setChartAccountName(displayItem.accountName);
    setShowChartModal(true);
  };

  const assessmentReportAvailable = (assessmentId?: string | null): boolean => {
    if (!assessmentId) return false;
    if (mixScope !== "my") return true;
    const related = levelMixData
      .flatMap(item => item.accounts || [])
      .filter(acc => acc.assessmentId === assessmentId);
    if (related.length === 0) return true;
    return related.some(
      acc => acc.publishedStatus === "published" || Boolean(acc.orgPublishedAt)
    );
  };

  const formatAccountDisplay = (
    accounts: AccountItem[] | undefined,
    levelItem: LevelMixItem
  ) => {
    if (!accounts || accounts.length === 0) return "-";

    const accountMap = new Map<string, AccountItem[]>();
    accounts.forEach(acc => {
      if (acc.isCurrentLevel === true) {
        if (!accountMap.has(acc.name)) accountMap.set(acc.name, []);
        accountMap.get(acc.name)!.push({
          ...acc,
          date: acc.publishedAt ? new Date(acc.publishedAt) : new Date(0),
        } as any);
      }
    });

    const displayItems: any[] = [];
    accountMap.forEach((accountOccurrences, accountName) => {
      accountOccurrences.sort(
        (a: any, b: any) => b.date.getTime() - a.date.getTime()
      );
      const mostRecent = accountOccurrences[0];
      let totalOccurrencesAcrossAllLevels = 0;
      levelMixData.forEach(li => {
        li.accounts?.forEach(acc => {
          if (acc.name === accountName) totalOccurrencesAcrossAllLevels++;
        });
      });
      displayItems.push({
        accountId: (mostRecent as any).id,
        accountName,
        mostRecentDate: mostRecent.publishedAt,
        allOccurrences: accountOccurrences,
        additionalCount: totalOccurrencesAcrossAllLevels - 1,
        assessmentOrigin: (mostRecent as any).assessmentOrigin,
        youLevel: (mostRecent as any).youLevel,
        publishedLevel: (mostRecent as any).publishedLevel,
        publishedStatus: (mostRecent as any).publishedStatus,
        level: parseLevelNumber(
          levelMixData.find(item =>
            item.accounts?.some(acc => acc.name === accountName)
          )?.level || 0
        ),
      });
    });

    const isUnknownRow = isUnknownLevelMixRow(levelItem.level);
    const maxVisibleBadges = isUnknownRow ? 5 : 40;
    const visibleItems = displayItems.slice(0, maxVisibleBadges);
    const remainingItems = displayItems.slice(maxVisibleBadges);
    const remainingCount = remainingItems.length;
    const remainingNamesTooltip = remainingItems
      .map(item => item.accountName)
      .filter(Boolean)
      .join(", ");

    const handleShowMore = () => {
      if (isUnknownRow) return;
      setModalAccountsData({
        displayItems,
        levelItem,
        level: levelItem.level,
        count: levelItem.count || 0,
        percentage: levelItem.percentage,
      });
      setShowAccountsModal(true);
    };

    return (
      <div className="flex flex-wrap gap-2">
        {visibleItems.map((displayItem, idx) => {
          const isIncomplete = incompleteAccountIds.has(
            String(displayItem.accountId || "")
          );
          const label = isIncomplete ? (
            <>
              {displayItem.accountName}{" "}
              <span className="text-amber-500">⚠</span>
            </>
          ) : (
            displayItem.accountName
          );
          const title = isIncomplete
            ? `${displayItem.accountName} (incomplete)`
            : displayItem.accountName;
          const highlightClass =
            highlightAccountId && displayItem.accountId === highlightAccountId
              ? "bg-violet-200 ring-2 ring-violet-400"
              : "bg-gray-100";

          if (isUnknownRow) {
            return (
              <span
                key={displayItem.accountId || idx}
                id={
                  displayItem.accountId
                    ? `levelmix-account-${displayItem.accountId}`
                    : undefined
                }
                data-account-id={displayItem.accountId || undefined}
                className={`px-2 py-0.5 rounded-full text-xs text-blue-600 font-medium whitespace-nowrap ${highlightClass}`}
                title={title}
              >
                {label}
              </span>
            );
          }

          return (
            <button
              key={displayItem.accountId || idx}
              id={
                displayItem.accountId
                  ? `levelmix-account-${displayItem.accountId}`
                  : undefined
              }
              data-account-id={displayItem.accountId || undefined}
              onClick={() => handleAccountClick(displayItem)}
              className={`px-2 py-0.5 rounded-full text-xs text-blue-600 hover:bg-gray-200 font-medium cursor-pointer transition-colors whitespace-nowrap ${highlightClass}`}
              title={title}
            >
              {label}
            </button>
          );
        })}
        {remainingCount > 0 &&
          (isUnknownRow ? (
            <span
              className="px-2 py-0.5 bg-gray-200 rounded-full text-xs text-gray-700 font-medium whitespace-nowrap cursor-default"
              title={remainingNamesTooltip}
            >
              +{remainingCount} More
            </span>
          ) : (
            <button
              onClick={handleShowMore}
              className="px-2 py-0.5 bg-gray-200 rounded-full text-xs text-gray-700 hover:bg-gray-300 font-medium cursor-pointer transition-colors whitespace-nowrap"
            >
              +{remainingCount} More
            </button>
          ))}
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="level-mix-container flex flex-col gap-4">
      {isPageShimmerLoading ? (
        <>
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-4 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <ShimmerBlock className="h-9 w-56" />
              <ShimmerBlock className="h-9 w-64" />
            </div>
            <div className="flex items-center gap-2">
              <ShimmerBlock className="h-8 w-28" />
              <ShimmerBlock className="h-8 w-6" />
              <ShimmerBlock className="h-8 w-28" />
              <ShimmerBlock className="h-8 w-16" />
              <ShimmerBlock className="h-4 w-72" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ShimmerBlock className="h-4 w-36" />
            <ShimmerBlock className="h-5 w-10" />
            <ShimmerBlock className="h-8 w-44" />
            <ShimmerBlock className="h-8 w-44" />
            <ShimmerBlock className="h-8 w-44" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[...Array(5)].map((_, idx) => (
              <div
                key={idx}
                className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm space-y-3"
              >
                <ShimmerBlock className="h-4 w-24" />
                <ShimmerBlock className="h-8 w-14" />
                <ShimmerBlock className="h-3 w-28" />
              </div>
            ))}
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-5">
            <ShimmerBlock className="h-5 w-72" />
            <div className="flex items-end justify-between gap-5 overflow-hidden">
              <ShimmerBlock className="h-44 w-40 rounded-full" />
              <ShimmerBlock className="h-44 w-40 rounded-full" />
              <ShimmerBlock className="h-64 w-64 rounded-full" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <ShimmerBlock className="h-4 w-48" />
            </div>
            <div className="p-4 space-y-3">
              {[...Array(6)].map((_, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-3">
                  <ShimmerBlock className="h-7 col-span-2" />
                  <ShimmerBlock className="h-7 col-span-2" />
                  <ShimmerBlock className="h-7 col-span-2" />
                  <ShimmerBlock className="h-7 col-span-6" />
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          {/* ── Control Banner ── */}
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 shadow-sm">
            {/* Row 1: Scope + Formal/Team */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3 flex-wrap">
                {showScopeToggle ? (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Scope:</span>
                    <TabsComponent
                      tabs={primaryScopeTabs}
                      activeTab={
                        useNestedPublishedScopeUi
                          ? mixScope === "my"
                            ? "my"
                            : "published"
                          : mixScope
                      }
                      onTabChange={(id: string) => {
                        if (useNestedPublishedScopeUi) {
                          if (id === "my") {
                            publishedKindPinnedRef.current = true;
                            setMixScope("my");
                            onMixScopeChange?.("my");
                            setPortfolioFilter("all");
                            resetMixCaches();
                            setAssessmentsList([]);
                            return;
                          }

                          // Already on Published — keep current Formal/Team
                          if (isPublishedOrgMixScope(mixScope)) return;

                          // My → Published: pick Formal/Team from newest publish
                          publishedKindPinnedRef.current = false;
                          resetMixCaches();
                          setAssessmentsList([]);
                          setMixScope("formal");
                          onMixScopeChange?.("formal");
                          return;
                        }

                        const scope = id as LevelMixScope;
                        setMixScope(scope);
                        onMixScopeChange?.(scope);
                        if (scope === "my") setPortfolioFilter("all");
                        resetMixCaches();
                        setAssessmentsList([]);
                      }}
                      variant="secondary-segmented"
                      segmentedLayout="fit"
                      className="rounded-full"
                    />
                    {isPublishedOrgMixScope(mixScope) && showPortfolioFilter ? (
                      <ToggleSwitch
                        id="level-mix-my-portfolio-toggle"
                        label="My Portfolio"
                        checked={portfolioFilter === "myPortfolio"}
                        onChange={checked => {
                          setPortfolioFilter(checked ? "myPortfolio" : "all");
                          resetMixCaches();
                          setAssessmentsList([]);
                        }}
                      />
                    ) : null}
                  </div>
                ) : (
                  <div />
                )}
              </div>

              {useNestedPublishedScopeUi &&
              isPublishedOrgMixScope(mixScope) &&
              publishedKindTabs.length > 1 ? (
                <div className="grid grid-cols-2 overflow-visible rounded-md border border-gray-200 bg-white text-sm font-medium">
                  {publishedKindTabs.map((tab, index, tabs) => {
                    const isActive = mixScope === tab.id;
                    const isFirst = index === 0;
                    const isLast = index === tabs.length - 1;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => {
                          if (
                            !isPublishedOrgMixScope(tab.id) ||
                            tab.id === mixScope
                          ) {
                            return;
                          }
                          publishedKindPinnedRef.current = true;
                          setMixScope(tab.id);
                          onMixScopeChange?.(tab.id);
                          resetMixCachesKeepPublished();
                          applyPublishedScopeFilter(tab.id);
                          setAssessmentsList([]);
                        }}
                        className={`px-3 py-2 transition-colors ${
                          isFirst ? "rounded-l-[5px]" : ""
                        } ${isLast ? "rounded-r-[5px]" : ""} ${
                          isActive
                            ? "bg-[#f5edfc] font-semibold text-[#974ae4]"
                            : "bg-white text-[#4b5563] hover:bg-[#f3f4f6]"
                        }`}
                      >
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>

            {/* Row 2: From → To date picker + Reset + hint; Consolidated / By Assessment on the right */}
            <div className="flex items-center gap-x-3 gap-y-2 flex-wrap">
              <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-600 leading-none">From</span>
              <DatePicker
                name="levelMixStartDate"
                value={toDateInputValue(dateRange.startDate)}
                max={toDateInputValue(dateRange.endDate) || undefined}
                onChange={e => {
                  setLimitToLatestThree(false);
                  setDateRange({
                    ...dateRange,
                    startDate: isoFromDateInput(e.target.value),
                  });
                }}
                className="mb-0 level-mix-date-picker"
                placeholder="MM/DD/YYYY"
              />
              <span className="text-gray-400">→</span>
              <DatePicker
                name="levelMixEndDate"
                value={toDateInputValue(dateRange.endDate)}
                min={toDateInputValue(dateRange.startDate) || undefined}
                onChange={e => {
                  setLimitToLatestThree(false);
                  setDateRange({
                    ...dateRange,
                    endDate: isoFromDateInput(e.target.value),
                  });
                }}
                className="mb-0 level-mix-date-picker"
                placeholder="MM/DD/YYYY"
              />
              <button
                onClick={() => {
                  if (isPublishedOrgMixScope(mixScope)) {
                    hasAutoExpanded.current = false;
                    const spanned = dateRangeSpanningLastPublished(
                      publishedWindowSource(),
                      3
                    );
                    if (spanned) {
                      hasAutoExpanded.current = true;
                      setLimitToLatestThree(true);
                      setDateRange(spanned);
                      return;
                    }
                  }
                  if (mixScope === "my") {
                    hasAutoExpanded.current = false;
                    const spanned = dateRangeSpanningLastPublished(
                      allMySubmitsRef.current,
                      3
                    );
                    if (spanned) {
                      hasAutoExpanded.current = true;
                      lastLevelMixKeyRef.current = "";
                      setLimitToLatestThree(true);
                      setDateRange(spanned);
                      return;
                    }
                  }
                  hasAutoExpanded.current = false;
                  setLimitToLatestThree(true);
                  setDateRange({
                    startDate: utcMonthsAgoInputValue(1),
                    endDate: utcTodayInputValue(),
                  });
                }}
                className="px-3 py-1 text-sm font-medium text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Reset
              </button>
              {isPublishedOrgMixScope(mixScope) ? (
                <span className="text-sm text-gray-400 italic">
                  {limitToLatestThree
                    ? mixScope === "team"
                      ? "Auto-set to span lastest 3 published Team assessments"
                      : mixScope === "formal"
                        ? "Auto-set to span lastest 3 published Formal assessments"
                        : "Auto-set to span lastest 3 published assessments"
                    : "Showing all publishes in the selected date range"}
                </span>
              ) : (
                <span className="text-sm text-gray-400 italic">
                  Auto-set to span last 3 submitted Team and Formal assessments
                </span>
              )}
              {/* Consolidated / By Assessment — underline tab style */}
              <div className="ml-auto flex shrink-0 items-center self-center">
                <TabsComponent
                  tabs={[
                    { id: "consolidated", label: "Consolidated" },
                    { id: "byAssessment", label: "By Assessment" },
                  ]}
                  activeTab={viewMode}
                  onTabChange={(id: string) => {
                    const next =
                      id === "byAssessment" ? "byAssessment" : "consolidated";
                    setViewMode(next);
                    if (next === "consolidated") {
                      setSelectedAssessmentId(null);
                    } else if (
                      assessmentsList.length > 0 &&
                      !selectedAssessmentId
                    ) {
                      setSelectedAssessmentId(assessmentsList[0].id);
                    }
                  }}
                  variant="tab-underline"
                  className="level-mix-kind-tabs"
                />
              </div>
            </div>

            {/* Row 3: Assessment dropdown (byAssessment mode only) */}
            {viewMode === "byAssessment" && (
              <div className="mt-3">
                <div className="flex items-center gap-3">
                  <select
                    value={selectedAssessmentId || ""}
                    onChange={e =>
                      setSelectedAssessmentId(e.target.value || null)
                    }
                    disabled={
                      assessmentsLoading || assessmentsList.length === 0
                    }
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  >
                    <option value="">
                      {assessmentsLoading
                        ? "Loading..."
                        : assessmentsList.length === 0
                          ? "No assessments found"
                          : "Select assessment"}
                    </option>
                    {assessmentsList
                      .slice(
                        0,
                        mixScope === "my" || !limitToLatestThree ? undefined : 3
                      )
                      .map(assessment => {
                        const publishedAt =
                          assessment.publishedAt || assessment.published_at;
                        const publishedDate = publishedAt
                          ? formatDate(publishedAt)
                          : "";
                        const title = normalizeTeamAssessmentDisplayName(
                          assessment.title,
                          "Untitled Assessment"
                        );
                        return (
                          <option key={assessment.id} value={assessment.id}>
                            {publishedDate
                              ? `${title} — Published On: ${publishedDate}`
                              : title}
                          </option>
                        );
                      })}
                  </select>
                  {selectedAssessmentId && mixScope !== "my" && (
                    <button
                      onClick={() => {
                        if (!assessmentReportAvailable(selectedAssessmentId))
                          return;
                        const assessment = assessmentsList.find(
                          a => a.id === selectedAssessmentId
                        );
                        if (assessment) {
                          openReportModal(assessment);
                        }
                      }}
                      disabled={
                        !assessmentReportAvailable(selectedAssessmentId)
                      }
                      title={
                        assessmentReportAvailable(selectedAssessmentId)
                          ? undefined
                          : "Available after publish"
                      }
                      className={`whitespace-nowrap text-sm font-medium ${
                        assessmentReportAvailable(selectedAssessmentId)
                          ? "text-indigo-600 hover:text-indigo-700 hover:underline"
                          : "text-gray-400 cursor-not-allowed"
                      }`}
                    >
                      View Report →
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Recent Assessments bar (consolidated mode only) ── */}
          {assessmentsList.length > 0 && viewMode === "consolidated" && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                Recent Assessments
              </span>
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-100 text-purple-700">
                NEW
              </span>
              <span className="text-xs text-gray-400">—</span>
              <span className="text-xs text-gray-500 italic mr-1">
                Click to jump
              </span>
              {assessmentsList.slice(0, 3).map(assessment => {
                const publishedAt =
                  assessment.publishedAt || assessment.published_at;
                const recentViewReportEnabled =
                  mixScope !== "my" && assessmentReportAvailable(assessment.id);
                return (
                  <span
                    key={assessment.id}
                    className="inline-flex items-center gap-1.5"
                  >
                    <button
                      onClick={() => {
                        setSelectedAssessmentId(assessment.id);
                        setViewMode("byAssessment");
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
                    >
                      <span className="truncate max-w-[160px]">
                        {normalizeTeamAssessmentDisplayName(
                          assessment.title,
                          "Untitled"
                        )}
                      </span>
                      <span className="text-purple-400">·</span>
                      <span className="whitespace-nowrap">
                        {publishedAt ? formatDate(publishedAt) : ""}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!recentViewReportEnabled) return;
                        openReportModal(assessment);
                      }}
                      disabled={!recentViewReportEnabled}
                      title={
                        mixScope === "my"
                          ? "Report not available for My submissions"
                          : recentViewReportEnabled
                            ? undefined
                            : "Available after publish"
                      }
                      className={`text-xs font-medium whitespace-nowrap ${
                        recentViewReportEnabled
                          ? "text-indigo-500 hover:text-indigo-700 hover:underline"
                          : "text-gray-400 cursor-not-allowed"
                      }`}
                    >
                      View Report →
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          {/* ── 5-card Stats Strip ── */}
          {(summaryData.assessmentCount > 0 ||
            summaryData.totalTargeted > 0) && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {/* Accounts Targeted */}
              <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg flex-shrink-0">
                    <Building2 className="h-5 w-5 text-gray-500" />
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider leading-tight">
                      Accounts Targeted
                    </p>
                    <p className="text-2xl font-bold text-gray-900 mt-0.5">
                      {summaryData.totalTargeted}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      across {summaryData.assessmentCount} assessment
                      {summaryData.assessmentCount !== 1 ? "s" : ""}
                    </p>
                  </div>
                </div>
              </div>

              {/* Complete */}
              {(() => {
                const complete = summaryData.accountCount;
                const total = summaryData.totalTargeted || complete;
                const pct =
                  total > 0 ? Math.round((complete / total) * 100) : 0;
                return (
                  <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-green-50 rounded-lg flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-green-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider leading-tight">
                          Complete (100% participation)
                        </p>
                        <p className="text-2xl font-bold text-gray-900 mt-0.5">
                          {complete}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          in levels mix · {pct}%
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Incomplete */}
              {(() => {
                const complete = summaryData.accountCount;
                const total = summaryData.totalTargeted || complete;
                const incomplete = incompleteKpiCount;
                const pct =
                  total > 0 ? Math.round((incomplete / total) * 100) : 0;
                return (
                  <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-orange-50 rounded-lg flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-orange-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider leading-tight">
                          Incomplete
                        </p>
                        <p className="text-2xl font-bold text-gray-900 mt-0.5">
                          {incomplete}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          missing responses · {pct}%
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Sponsors */}
              <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-50 rounded-lg flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-purple-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider leading-tight">
                      Sponsors
                    </p>
                    <p className="text-2xl font-bold text-gray-900 mt-0.5">
                      {summaryData.sponsorCount}
                    </p>
                  </div>
                </div>
              </div>

              {/* Unassessed */}
              <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-slate-100 rounded-lg flex-shrink-0">
                    <ClipboardX className="h-5 w-5 text-slate-500" />
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider leading-tight">
                      Unassessed
                    </p>
                    <p className="text-2xl font-bold text-gray-900 mt-0.5">
                      {summaryData.unassessedCount}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Level mix data section ── */}
          {levelMixLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading level mix data...</p>
            </div>
          ) : !hasLevelMixAccounts &&
            !hasIncompleteAccounts &&
            assessmentsList.length === 0 ? (
            <EngagementEmptyPanel
              variant="sponsor"
              icon={BarChart3}
              title="No level mix data found"
              hint={
                mixScope === "my"
                  ? "No submitted Team or Formal assessments in this period"
                  : "No published assessments found for the selected date range"
              }
            />
          ) : (
            <>
              {/* Pie slice filter banner */}
              {activePieSlice && (
                <div className="flex items-center justify-between px-4 py-2 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-700">
                  <span>
                    Filtered by{" "}
                    <strong>{getLevelCompactName(activePieSlice)}</strong> —
                    showing accounts at this level only
                  </span>
                  <button
                    onClick={() => setActivePieSlice(null)}
                    className="flex items-center gap-1 text-xs font-medium hover:text-purple-900"
                  >
                    <X size={13} /> Clear filter
                  </button>
                </div>
              )}

              {/* ── Levels Mix Over Time ── */}
              {(chartTrendData.length > 0 || !trendLoading) &&
                (() => {
                  const isSampleMode =
                    !trendLoading &&
                    chartTrendData.length === 0 &&
                    viewMode === "consolidated" &&
                    assessmentsList.length === 0;
                  const SAMPLE_KEYS = ["L0", "L1", "L2", "L3", "L4", "L5"];
                  const SAMPLE_NAMES = [
                    "New",
                    "Acquainted",
                    "Reactive",
                    "Proactive",
                    "Referenceable",
                    "Strategic",
                  ];
                  const SAMPLE_RAW = [
                    [5, 9, 10, 8, 3, 1],
                    [4, 8, 11, 9, 4, 1],
                    [3, 7, 10, 10, 5, 2],
                  ];
                  const sampleData = SAMPLE_RAW.map((vals, i) => {
                    const total = vals.reduce((a, b) => a + b, 0);
                    return {
                      assessment: {
                        id: `sample-${i}`,
                        title: `Sample Assessment ${["A", "B", "C"][i]}`,
                        publishedAt: null,
                      },
                      levelDistribution: vals.map((count, li) => ({
                        level: SAMPLE_KEYS[li],
                        levelName: SAMPLE_NAMES[li],
                        count,
                        percentage: Math.round((count / total) * 100),
                      })),
                      totalTargeted: total,
                    };
                  });
                  const displayData = isSampleMode
                    ? sampleData
                    : chartTrendData;

                  return (
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      {/* Header */}
                      <div className="flex items-center gap-2 flex-wrap pb-4 mb-5 border-b border-gray-200">
                        <BarChart3 className="h-4 w-4 text-gray-500 flex-shrink-0" />
                        <h3 className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                          Levels Mix Over Time
                        </h3>
                        <span className="text-gray-300">·</span>
                        <p className="text-xs text-gray-500">
                          Oldest → Current · Only fully-participated accounts ·
                          Click current (right) pie to filter table below
                        </p>
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-100 text-purple-700 ml-1">
                          NEW
                        </span>
                        {viewMode === "byAssessment" &&
                          selectedAssessmentId &&
                          (() => {
                            const sel = assessmentsList.find(
                              a => a.id === selectedAssessmentId
                            );
                            return sel ? (
                              <span className="ml-auto text-xs text-indigo-600 font-medium whitespace-nowrap">
                                Showing: {sel.title}
                              </span>
                            ) : null;
                          })()}
                      </div>

                      {/* Sample mode banner */}
                      {isSampleMode && (
                        <div className="flex items-start gap-2 px-3 py-2.5 mb-5 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                          <svg
                            className="h-4 w-4 flex-shrink-0 mt-0.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <circle cx="12" cy="12" r="10" strokeWidth={2} />
                            <line
                              x1="12"
                              y1="8"
                              x2="12"
                              y2="12"
                              strokeWidth={2}
                            />
                            <line
                              x1="12"
                              y1="16"
                              x2="12.01"
                              y2="16"
                              strokeWidth={2}
                            />
                          </svg>
                          <div>
                            <strong>
                              No assessments found for this period.
                            </strong>{" "}
                            The chart below shows sample data for illustration
                            only. Adjust the date range or publish an assessment
                            to see real data.
                          </div>
                        </div>
                      )}

                      {/* < 3 assessments banner */}
                      {!isSampleMode &&
                        viewMode === "consolidated" &&
                        chartTrendData.length < 3 &&
                        !trendLoading && (
                          <div className="flex items-start gap-2 px-3 py-2.5 mb-6 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                            <svg
                              className="h-4 w-4 flex-shrink-0 mt-0.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            <span>
                              Full trend comparison will appear once a third
                              assessment exists within the selected range.
                              Showing{" "}
                              {chartTrendData.length === 1
                                ? "the most recent assessment"
                                : "the two most recent assessments"}
                              .
                            </span>
                          </div>
                        )}

                      {trendLoading && showInitialShimmer ? (
                        <div className="flex items-center justify-center py-6">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600" />
                        </div>
                      ) : (
                        <div style={{ position: "relative" }}>
                          <div
                            style={{
                              opacity: isSampleMode ? 0.45 : 1,
                              pointerEvents: isSampleMode ? "none" : "auto",
                            }}
                          >
                            {(() => {
                              const slots = Array.from({ length: 3 }).map(
                                (_, slotIdx) => {
                                  const offset = 3 - displayData.length;
                                  const dataIdx = slotIdx - offset;
                                  const slot =
                                    dataIdx >= 0 ? displayData[dataIdx] : null;
                                  const isCurrentSlot = slotIdx === 2;
                                  const prevSlot =
                                    slotIdx > 0 && slotIdx - 1 - offset >= 0
                                      ? displayData[slotIdx - 1 - offset]
                                      : null;
                                  return {
                                    slot,
                                    isCurrentSlot,
                                    prevSlot,
                                    slotIdx,
                                  };
                                }
                              );

                              return (
                                <div
                                  style={{
                                    overflowX: "auto",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                  }}
                                >
                                  {/* Canvas row */}
                                  <div
                                    className="flex items-center"
                                    style={{ minWidth: "max-content" }}
                                  >
                                    {slots.map(
                                      ({
                                        slot,
                                        isCurrentSlot,
                                        prevSlot,
                                        slotIdx,
                                      }) => {
                                        const deltaCol =
                                          slotIdx > 0
                                            ? (() => {
                                                const gapW = isCurrentSlot
                                                  ? 140
                                                  : 100;
                                                if (slot && prevSlot) {
                                                  const currMap =
                                                    Object.fromEntries(
                                                      slot.levelDistribution.map(
                                                        (d: any) => [
                                                          d.level,
                                                          d.count || 0,
                                                        ]
                                                      )
                                                    );
                                                  const prevMap =
                                                    Object.fromEntries(
                                                      prevSlot.levelDistribution.map(
                                                        (d: any) => [
                                                          d.level,
                                                          d.count || 0,
                                                        ]
                                                      )
                                                    );
                                                  const allLevels = Array.from(
                                                    new Set([
                                                      ...Object.keys(currMap),
                                                      ...Object.keys(prevMap),
                                                    ])
                                                  );
                                                  const changes = allLevels
                                                    .map(lvl => ({
                                                      lvl,
                                                      diff:
                                                        ((currMap as any)[
                                                          lvl
                                                        ] || 0) -
                                                        ((prevMap as any)[
                                                          lvl
                                                        ] || 0),
                                                    }))
                                                    .filter(c => c.diff !== 0)
                                                    .sort(
                                                      (a, b) =>
                                                        Math.abs(b.diff) -
                                                        Math.abs(a.diff)
                                                    )
                                                    .slice(0, 3);
                                                  return (
                                                    <div
                                                      key={`d${slotIdx}`}
                                                      style={{
                                                        width: gapW,
                                                        height: 320,
                                                        flexShrink: 0,
                                                      }}
                                                      className="flex flex-col items-center justify-center gap-1"
                                                    >
                                                      <span
                                                        style={{
                                                          color: "#9ca3af",
                                                          fontSize: 22,
                                                          lineHeight: 1,
                                                        }}
                                                      >
                                                        ›
                                                      </span>
                                                      <div className="flex flex-col gap-0.5 items-center">
                                                        {changes.map(
                                                          ({ lvl, diff }) => (
                                                            <span
                                                              key={lvl}
                                                              style={{
                                                                fontSize: 10,
                                                                fontWeight: 500,
                                                                whiteSpace:
                                                                  "nowrap",
                                                                color:
                                                                  diff > 0
                                                                    ? "#059669"
                                                                    : "#dc2626",
                                                              }}
                                                            >
                                                              {diff > 0
                                                                ? "+"
                                                                : ""}
                                                              {diff} Level{" "}
                                                              {getLevelNum(lvl)}
                                                            </span>
                                                          )
                                                        )}
                                                      </div>
                                                    </div>
                                                  );
                                                }
                                                return (
                                                  <div
                                                    key={`d${slotIdx}`}
                                                    style={{
                                                      width: gapW,
                                                      height: 320,
                                                      flexShrink: 0,
                                                    }}
                                                  />
                                                );
                                              })()
                                            : null;

                                        const CW = isCurrentSlot ? 540 : 165;
                                        const pieEl = (() => {
                                          if (!slot) {
                                            return (
                                              <div
                                                style={{
                                                  width: CW,
                                                  height: 320,
                                                  flexShrink: 0,
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    width: 150,
                                                    height: 150,
                                                    borderRadius: "50%",
                                                    border:
                                                      "10px dashed #d1d5db",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                  }}
                                                >
                                                  <span
                                                    style={{
                                                      fontSize: 10,
                                                      color: "#9ca3af",
                                                      fontWeight: 500,
                                                      textAlign: "center",
                                                      lineHeight: 1.4,
                                                    }}
                                                  >
                                                    Not
                                                    <br />
                                                    available
                                                  </span>
                                                </div>
                                              </div>
                                            );
                                          }
                                          const pieSlices = levelMixRowsForPie(
                                            slot.levelDistribution || []
                                          )
                                            .filter(
                                              (d: any) => (d.count || 0) > 0
                                            )
                                            .map((d: any) => ({
                                              name: getLevelCompactName(
                                                d.level
                                              ),
                                              value: d.count || 0,
                                              level: d.level,
                                              percentage: d.percentage,
                                            }));

                                          const chartW = isCurrentSlot
                                            ? 540
                                            : 165;
                                          const chartH = isCurrentSlot
                                            ? 360
                                            : 165;
                                          const pcx = isCurrentSlot ? 270 : 82;
                                          const pcy = isCurrentSlot ? 178 : 82;
                                          const outer = isCurrentSlot
                                            ? 110
                                            : 65;
                                          const inner = isCurrentSlot ? 52 : 29;

                                          return (
                                            <div
                                              style={{
                                                width: CW,
                                                height: 320,
                                                flexShrink: 0,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                              }}
                                            >
                                              <PieChart
                                                width={chartW}
                                                height={chartH}
                                              >
                                                <Pie
                                                  key={`trend-pie-${trendAnimationTick}-${slot?.assessment?.id || slotIdx}-${isCurrentSlot ? "current" : "history"}`}
                                                  data={
                                                    pieSlices.length > 0
                                                      ? pieSlices
                                                      : [
                                                          {
                                                            name: "empty",
                                                            value: 1,
                                                          },
                                                        ]
                                                  }
                                                  cx={pcx}
                                                  cy={pcy}
                                                  innerRadius={inner}
                                                  outerRadius={outer}
                                                  dataKey="value"
                                                  onClick={
                                                    isCurrentSlot
                                                      ? (entry: any) =>
                                                          setActivePieSlice(
                                                            prev =>
                                                              prev ===
                                                              entry.level
                                                                ? null
                                                                : entry.level
                                                          )
                                                      : undefined
                                                  }
                                                  style={{
                                                    cursor: isCurrentSlot
                                                      ? "pointer"
                                                      : "default",
                                                  }}
                                                  label={
                                                    isCurrentSlot &&
                                                    pieSlices.length > 0
                                                      ? ({
                                                          cx: lcx,
                                                          cy: lcy,
                                                          midAngle,
                                                          outerRadius: or,
                                                          payload,
                                                        }: any) => {
                                                          const RADIAN =
                                                            Math.PI / 180;
                                                          const armR = or + 8;
                                                          const ax =
                                                            lcx +
                                                            armR *
                                                              Math.cos(
                                                                -midAngle *
                                                                  RADIAN
                                                              );
                                                          const ay =
                                                            lcy +
                                                            armR *
                                                              Math.sin(
                                                                -midAngle *
                                                                  RADIAN
                                                              );
                                                          const onRight =
                                                            ax >= lcx;
                                                          const lvlNum =
                                                            getLevelNum(
                                                              payload.level
                                                            );
                                                          const line1 =
                                                            getLevelCompactName(
                                                              lvlNum
                                                            );
                                                          const line2 = `${payload.value} · ${payload.percentage ?? 0}%`;
                                                          const bgColor =
                                                            getLevelLabelBg(
                                                              payload.level
                                                            );
                                                          const textColor =
                                                            getLevelLabelText(
                                                              payload.level
                                                            );
                                                          const BOX_W = 68;
                                                          const BOX_H = 34;
                                                          const PAD = 5;
                                                          const bx = onRight
                                                            ? ax + PAD
                                                            : ax - PAD - BOX_W;
                                                          const by =
                                                            ay - BOX_H / 2;
                                                          const edgeX =
                                                            lcx +
                                                            or *
                                                              Math.cos(
                                                                -midAngle *
                                                                  RADIAN
                                                              );
                                                          const edgeY =
                                                            lcy +
                                                            or *
                                                              Math.sin(
                                                                -midAngle *
                                                                  RADIAN
                                                              );
                                                          const lineEndX =
                                                            onRight
                                                              ? bx
                                                              : bx + BOX_W;
                                                          return (
                                                            <g>
                                                              <line
                                                                x1={edgeX}
                                                                y1={edgeY}
                                                                x2={lineEndX}
                                                                y2={ay}
                                                                stroke={getLevelPieColor(
                                                                  payload.level
                                                                )}
                                                                strokeWidth={1}
                                                                opacity={0.4}
                                                              />
                                                              <rect
                                                                x={bx}
                                                                y={by}
                                                                width={BOX_W}
                                                                height={BOX_H}
                                                                rx={4}
                                                                ry={4}
                                                                fill={bgColor}
                                                              />
                                                              <text
                                                                x={
                                                                  bx + BOX_W / 2
                                                                }
                                                                y={by + 12}
                                                                fill={textColor}
                                                                textAnchor="middle"
                                                                style={{
                                                                  fontSize: 10,
                                                                  fontWeight: 600,
                                                                }}
                                                              >
                                                                {line1}
                                                              </text>
                                                              <text
                                                                x={
                                                                  bx + BOX_W / 2
                                                                }
                                                                y={by + 26}
                                                                fill={textColor}
                                                                textAnchor="middle"
                                                                style={{
                                                                  fontSize: 10,
                                                                  fontWeight: 700,
                                                                }}
                                                              >
                                                                {line2}
                                                              </text>
                                                            </g>
                                                          );
                                                        }
                                                      : false
                                                  }
                                                  labelLine={false}
                                                >
                                                  {pieSlices.length > 0 ? (
                                                    pieSlices.map(
                                                      (
                                                        entry: any,
                                                        idx: number
                                                      ) => (
                                                        <Cell
                                                          key={idx}
                                                          fill={getLevelPieColor(
                                                            entry.level
                                                          )}
                                                          opacity={
                                                            isCurrentSlot &&
                                                            activePieSlice &&
                                                            activePieSlice !==
                                                              entry.level
                                                              ? 0.22
                                                              : 1
                                                          }
                                                          stroke={
                                                            isCurrentSlot &&
                                                            activePieSlice ===
                                                              entry.level
                                                              ? "#1e293b"
                                                              : "#fff"
                                                          }
                                                          strokeWidth={2}
                                                        />
                                                      )
                                                    )
                                                  ) : (
                                                    <Cell
                                                      fill="#e5e7eb"
                                                      stroke="#fff"
                                                      strokeWidth={1}
                                                    />
                                                  )}
                                                </Pie>
                                                <RechartsTooltip
                                                  formatter={(
                                                    value: any,
                                                    name: any,
                                                    props: any
                                                  ) => [
                                                    `${value} accounts (${props.payload.percentage ?? ""}%)`,
                                                    name,
                                                  ]}
                                                />
                                              </PieChart>
                                            </div>
                                          );
                                        })();

                                        return (
                                          <div
                                            key={slotIdx}
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                            }}
                                          >
                                            {deltaCol}
                                            {pieEl}
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>

                                  {/* Title row */}
                                  <div
                                    className="flex"
                                    style={{
                                      marginTop: 10,
                                      minWidth: "max-content",
                                    }}
                                  >
                                    {slots.map(
                                      ({ slot, isCurrentSlot, slotIdx }) => {
                                        const spacerW =
                                          slotIdx > 0
                                            ? isCurrentSlot
                                              ? 140
                                              : 100
                                            : 0;
                                        const titleW = isCurrentSlot
                                          ? 540
                                          : 165;
                                        const publishedAt =
                                          slot?.assessment?.publishedAt ||
                                          slot?.assessment?.published_at;
                                        const rawTitle =
                                          normalizeTeamAssessmentDisplayName(
                                            slot?.assessment?.title,
                                            "Untitled"
                                          );
                                        const titleText = slot
                                          ? rawTitle.length > 26
                                            ? rawTitle.slice(0, 24) + "…"
                                            : rawTitle
                                          : null;
                                        const complete = slot
                                          ? levelMixRowsForPie(
                                              slot.levelDistribution || []
                                            ).reduce(
                                              (s: number, d: any) =>
                                                s + (d.count || 0),
                                              0
                                            )
                                          : 0;
                                        return (
                                          <div
                                            key={slotIdx}
                                            style={{ display: "flex" }}
                                          >
                                            {slotIdx > 0 && (
                                              <div
                                                style={{
                                                  width: spacerW,
                                                  flexShrink: 0,
                                                }}
                                              />
                                            )}
                                            <div
                                              style={{
                                                width: titleW,
                                                flexShrink: 0,
                                                textAlign: "center",
                                              }}
                                            >
                                              {slot ? (
                                                <>
                                                  <div
                                                    style={{
                                                      fontSize: 12,
                                                      fontWeight: 600,
                                                      color: "#374151",
                                                      overflow: "hidden",
                                                      textOverflow: "ellipsis",
                                                      whiteSpace: "nowrap",
                                                    }}
                                                  >
                                                    {titleText}
                                                  </div>
                                                  <div
                                                    style={{
                                                      fontSize: 10,
                                                      color: "#6b7280",
                                                      fontFamily: "monospace",
                                                      marginTop: 2,
                                                    }}
                                                  >
                                                    {publishedAt
                                                      ? formatDate(publishedAt)
                                                      : ""}
                                                  </div>
                                                  {slot.totalTargeted != null &&
                                                    slot.totalTargeted > 0 && (
                                                      <div
                                                        style={{
                                                          fontSize: 10,
                                                          color: "#6b7280",
                                                          marginTop: 2,
                                                        }}
                                                      >
                                                        <strong
                                                          style={{
                                                            color: "#374151",
                                                          }}
                                                        >
                                                          {complete}
                                                        </strong>{" "}
                                                        of {slot.totalTargeted}{" "}
                                                        complete
                                                      </div>
                                                    )}
                                                </>
                                              ) : (
                                                <>
                                                  <div
                                                    style={{
                                                      fontSize: 11,
                                                      color: "#9ca3af",
                                                      fontStyle: "italic",
                                                    }}
                                                  >
                                                    {(() => {
                                                      const current =
                                                        displayData[
                                                          displayData.length - 1
                                                        ];
                                                      const currentDate =
                                                        current?.assessment
                                                          ?.publishedAt ||
                                                        current?.assessment
                                                          ?.published_at;
                                                      if (!currentDate)
                                                        return "—";
                                                      const monthsBack =
                                                        2 - slotIdx;
                                                      const d = new Date(
                                                        currentDate
                                                      );
                                                      d.setMonth(
                                                        d.getMonth() -
                                                          monthsBack
                                                      );
                                                      return `~${formatDate(d.toISOString())}`;
                                                    })()}
                                                  </div>
                                                  <div
                                                    style={{
                                                      fontSize: 11,
                                                      color: "#9ca3af",
                                                      fontStyle: "italic",
                                                    }}
                                                  >
                                                    — not available
                                                  </div>
                                                </>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Shared legend */}
                            {(() => {
                              const latestSlotWithLevels = [...displayData]
                                .reverse()
                                .find(
                                  (slot: any) =>
                                    Array.isArray(slot?.levelDistribution) &&
                                    slot.levelDistribution.length > 0
                                );

                              const legendSource = latestSlotWithLevels
                                ?.levelDistribution?.length
                                ? latestSlotWithLevels.levelDistribution
                                : levelMixData;

                              const legendItems = (legendSource || [])
                                .map((d: any) => {
                                  const num = parseInt(
                                    String(d.level || "").replace(
                                      /[^0-9]/g,
                                      ""
                                    ),
                                    10
                                  );
                                  const safeNum = isNaN(num) ? 0 : num;
                                  return {
                                    label: getAccountLevelCompactLabel(
                                      safeNum,
                                      d.levelName
                                    ),
                                    level: d.level,
                                    levelNum: safeNum,
                                  };
                                })
                                .sort(
                                  (a: any, b: any) => a.levelNum - b.levelNum
                                );
                              if (legendItems.length === 0) return null;
                              return (
                                <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4 pt-3 border-t border-gray-300">
                                  {legendItems.map(
                                    (entry: any, idx: number) => (
                                      <button
                                        key={idx}
                                        onClick={() =>
                                          setActivePieSlice(prev =>
                                            prev === entry.level
                                              ? null
                                              : entry.level
                                          )
                                        }
                                        className={`flex items-center gap-2 text-xs text-left transition-opacity ${activePieSlice && activePieSlice !== entry.level ? "opacity-40" : ""}`}
                                      >
                                        <span
                                          className="w-3 h-3 rounded-full flex-shrink-0"
                                          style={{
                                            backgroundColor: getLevelPieColor(
                                              entry.level
                                            ),
                                          }}
                                        />
                                        <span className="font-medium text-black">
                                          {entry.label}
                                        </span>
                                      </button>
                                    )
                                  )}
                                </div>
                              );
                            })()}
                          </div>

                          {/* SAMPLE watermark */}
                          {isSampleMode && (
                            <div
                              style={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                pointerEvents: "none",
                                zIndex: 10,
                              }}
                            >
                              <div
                                style={{
                                  border: "3px dashed #94a3b8",
                                  color: "#94a3b8",
                                  fontSize: 18,
                                  fontWeight: 800,
                                  letterSpacing: "0.25em",
                                  padding: "8px 20px",
                                  borderRadius: 8,
                                  opacity: 0.55,
                                  transform: "rotate(-22deg)",
                                }}
                              >
                                SAMPLE
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}

              {/* ── Levels Distribution Table ── */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                  <span className="text-sm font-semibold text-gray-800">
                    {mixScope === "my"
                      ? "My Levels Distribution"
                      : "Published Levels Distribution"}
                  </span>
                  <span className="text-xs text-gray-500 flex items-center gap-1 flex-wrap justify-end">
                    {summaryData.accountCount > 0 && (
                      <>
                        Showing{" "}
                        <strong className="text-gray-700">
                          {summaryData.accountCount}
                        </strong>{" "}
                        complete accounts
                        {activePieSlice && (
                          <>
                            {" "}
                            · filtered by {getLevelCompactName(activePieSlice)}
                          </>
                        )}
                        &nbsp;·&nbsp; click current pie ↑ to filter
                        {hasIncompleteAccounts && (
                          <>
                            {" "}
                            &nbsp;·&nbsp;{" "}
                            <span className="text-amber-500 font-medium">
                              {incompleteAccountCount} incomplete
                            </span>{" "}
                            — see below ↓
                          </>
                        )}
                      </>
                    )}
                    {incompleteAccountIds.size > 0 && (
                      <>
                        &nbsp;·&nbsp; <span className="text-amber-500">⚠</span>{" "}
                        = incomplete (also listed below)
                      </>
                    )}
                    {(() => {
                      const hasStale = levelMixData.some(item =>
                        item.accounts?.some(
                          (a: any) => a.isCurrentLevel === false || a.stale
                        )
                      );
                      // Incomplete legend already uses ⚠; avoid a duplicate prior-level note.
                      return hasStale && incompleteAccountIds.size === 0 ? (
                        <>
                          &nbsp;·&nbsp;{" "}
                          <span className="text-amber-500">⚠</span> = prior
                          assessment level (incomplete in latest)
                        </>
                      ) : null;
                    })()}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[15rem]">
                          Account Level
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[10rem]">
                          No. Of Accounts
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[10rem]">
                          % of Accounts
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Account Names
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(activePieSlice
                        ? levelMixData.filter(i => i.level === activePieSlice)
                        : levelMixData
                      )
                        .slice()
                        .sort(sortLevelMixRows)
                        .map((item, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap w-[15rem]">
                              {(() => {
                                if (isUnknownLevelMixRow(item.level)) {
                                  return (
                                    <span
                                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
                                      style={{
                                        backgroundColor: "#F3F4F6",
                                        color: "#4B5563",
                                      }}
                                    >
                                      Unknown
                                    </span>
                                  );
                                }
                                const chipLabel = getAccountLevelCompactLabel(
                                  item.level,
                                  item.levelName
                                );
                                return (
                                  <span
                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
                                    style={{
                                      backgroundColor: getLevelLabelBg(
                                        item.level
                                      ),
                                      color: getLevelLabelText(item.level),
                                    }}
                                  >
                                    {chipLabel}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap w-[10rem]">
                              <div className="text-sm font-medium text-gray-900">
                                {item.count || 0}
                              </div>
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap w-[10rem]">
                              <div className="text-sm font-medium text-gray-900">
                                {item.percentage !== null
                                  ? `${item.percentage}%`
                                  : "-"}
                              </div>
                            </td>
                            <td className="px-6 py-4" style={{ minWidth: 400 }}>
                              <div className="text-sm text-gray-900">
                                {formatAccountDisplay(item.accounts, item)}
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ── Incomplete Accounts Section ── */}
              {(() => {
                if (!hasIncompleteAccounts) return null;

                const incomplete = incompleteAccountCount;
                const detailedIncomplete = detailedIncompleteAccounts;
                const hasDetailedRows = detailedIncomplete.length > 0;

                const staleAccounts: any[] = [];
                levelMixData.forEach(item => {
                  (item.accounts || []).forEach((a: any) => {
                    if (a.isCurrentLevel === false || a.stale) {
                      staleAccounts.push({
                        name: a.name,
                        level: item.level,
                        staleInfo: a.stale || a.publishedAt,
                      });
                    }
                  });
                });
                const priorCount = hasDetailedRows
                  ? detailedIncomplete.filter((a: any) => a.hasPriorLevel)
                      .length
                  : staleAccounts.length;
                // "Not in levels mix" = incomplete and not in any L0–L5 current
                // row (Unknown row does not count). Override after unknown publish
                // places the account in L0–L5, so it must not stay in this count.
                const inMixAccountIds = new Set<string>();
                levelMixData.forEach(item => {
                  if (isUnknownLevelMixRow(item.level)) return;
                  (item.accounts || []).forEach((a: any) => {
                    if (a?.isCurrentLevel === false) return;
                    if (a?.id != null) inMixAccountIds.add(String(a.id));
                  });
                });
                const notInMixCount = hasDetailedRows
                  ? detailedIncomplete.filter(
                      (a: any) =>
                        !a.hasPriorLevel &&
                        !inMixAccountIds.has(String(a.id || ""))
                    ).length
                  : 0;
                const subtitleParts: string[] = [];
                if (notInMixCount > 0) {
                  subtitleParts.push(`${notInMixCount} not in levels mix`);
                }
                if (priorCount > 0) {
                  subtitleParts.push(`${priorCount} shown at prior level ⚠`);
                }
                const subtitle =
                  subtitleParts.length > 0 ? subtitleParts.join(" · ") : null;

                const detailedKeys = detailedIncomplete.map(incompleteRowKey);
                const allDetailedSelected =
                  detailedKeys.length > 0 &&
                  detailedKeys.every(k => incompleteSelectedKeys.has(k));
                const selectedCount = detailedKeys.filter(k =>
                  incompleteSelectedKeys.has(k)
                ).length;
                const showFormalSelect =
                  hasDetailedRows && showIncompleteFormalActions;
                const cbClass =
                  "h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500";

                return (
                  <div className="border border-amber-300 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-amber-50 border-b border-amber-200">
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        {showFormalSelect ? (
                          <input
                            type="checkbox"
                            className={cbClass}
                            checked={allDetailedSelected}
                            onChange={e => {
                              e.stopPropagation();
                              if (e.target.checked) {
                                setIncompleteSelectedKeys(
                                  new Set(detailedKeys)
                                );
                              } else {
                                setIncompleteSelectedKeys(new Set());
                              }
                            }}
                            aria-label="Select all incomplete accounts"
                          />
                        ) : null}
                        <button
                          type="button"
                          className="flex min-w-0 flex-1 items-center gap-2 border-0 bg-transparent p-0 text-left cursor-pointer"
                          onClick={() => setIncompleteOpen(o => !o)}
                        >
                          <Info className="h-3.5 w-3.5 flex-shrink-0 text-amber-700" />
                          <span className="text-sm font-semibold text-amber-700">
                            Incomplete Accounts ({incomplete})
                            {subtitle ? (
                              <span className="font-normal text-amber-600">
                                {" "}
                                — {subtitle}
                              </span>
                            ) : null}
                          </span>
                        </button>
                      </div>
                      <div className="flex flex-shrink-0 items-center gap-2">
                        <button
                          type="button"
                          className="border-0 bg-transparent p-0 text-xs text-amber-600 cursor-pointer"
                          onClick={() => setIncompleteOpen(o => !o)}
                        >
                          {incompleteOpen ? "▲ Hide" : "▼ Show"}
                        </button>
                        {showFormalSelect ? (
                          <>
                            <span className="text-xs text-amber-700">
                              {selectedCount} selected
                            </span>
                            <button
                              type="button"
                              disabled={
                                selectedCount === 0 ||
                                isCheckingFormalRunning ||
                                isFormalSubmitting
                              }
                              onClick={e => {
                                e.stopPropagation();
                                void handleIncompleteFormalClick();
                              }}
                              className="inline-flex h-7 items-center justify-center rounded-md bg-[#F5B07A] px-3 text-xs font-semibold text-white transition hover:bg-[#e89d5c] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {isCheckingFormalRunning || isFormalSubmitting ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                "Formal"
                              )}
                            </button>
                          </>
                        ) : null}
                      </div>
                    </div>
                    {incompleteOpen && (
                      <div className="divide-y divide-amber-100">
                        {hasDetailedRows ? (
                          detailedIncomplete.map((a: any, i: number) => {
                            const rowKey = incompleteRowKey(a);
                            const checked = incompleteSelectedKeys.has(rowKey);
                            return (
                              <div
                                key={`incomplete-${rowKey || i}`}
                                className="flex items-center gap-3 px-4 py-3 bg-white text-sm"
                              >
                                {showFormalSelect ? (
                                  <input
                                    type="checkbox"
                                    className={cbClass}
                                    checked={checked}
                                    onChange={e => {
                                      setIncompleteSelectedKeys(prev => {
                                        const next = new Set(prev);
                                        if (e.target.checked) next.add(rowKey);
                                        else next.delete(rowKey);
                                        return next;
                                      });
                                    }}
                                    aria-label={`Select ${a.name || "account"}`}
                                  />
                                ) : null}
                                <span className="font-semibold text-gray-900 min-w-[180px]">
                                  {a.name || "Unknown Account"}{" "}
                                  {a.hasPriorLevel ? (
                                    <span className="text-amber-500">⚠</span>
                                  ) : null}
                                </span>
                                <span
                                  className={`text-[13px] font-semibold px-3 py-0.5 rounded-full ${a.respondedCount === 0 ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-700"}`}
                                >
                                  {a.respondedCount} of {a.totalAssigned}{" "}
                                  responded
                                </span>
                                <span className="text-[13px] text-gray-500">
                                  Missing:{" "}
                                  <span className="text-red-600 font-medium">
                                    {a.missingExecutives.length > 0
                                      ? a.missingExecutives.join(", ")
                                      : "responses pending"}
                                  </span>
                                </span>
                                {a.assessmentName ? (
                                  <span className="text-xs text-gray-500 ml-1 whitespace-nowrap">
                                    ·{" "}
                                    {normalizeTeamAssessmentDisplayName(
                                      a.assessmentName,
                                      ""
                                    )}
                                  </span>
                                ) : null}
                                {a.hasPriorLevel &&
                                (a.priorLevel || a.priorQuarter) ? (
                                  <span className="text-xs px-3 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-300 ml-auto whitespace-nowrap font-semibold">
                                    Prior:{" "}
                                    {a.priorLevel
                                      ? getLevelCompactName(a.priorLevel)
                                      : "-"}
                                    {a.priorQuarter
                                      ? ` · ${a.priorQuarter}`
                                      : ""}
                                  </span>
                                ) : null}
                              </div>
                            );
                          })
                        ) : (
                          <>
                            {staleAccounts.map((a, i) => (
                              <div
                                key={i}
                                className="flex items-center gap-3 px-4 py-2.5 bg-white text-sm"
                              >
                                <span className="font-medium text-gray-800 min-w-[140px]">
                                  {a.name}{" "}
                                  <span className="text-amber-500">⚠</span>
                                </span>
                                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 ml-auto">
                                  Prior: {getLevelCompactName(a.level)}
                                </span>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </>
      )}

      {/* ── Accounts Modal ── */}
      <CommonModal
        isOpen={showAccountsModal}
        toggle={() => {
          setShowAccountsModal(false);
          setModalAccountsData(null);
        }}
        size="4xl"
        title={
          modalAccountsData
            ? `All Accounts - ${
                isUnknownLevelMixRow(modalAccountsData.level)
                  ? "Unknown"
                  : getLevelCompactName(modalAccountsData.level)
              }`
            : "All Accounts"
        }
      >
        {modalAccountsData && (
          <div className="level-mix-modal-content">
            <p className="text-sm text-gray-600 mb-4">
              Click on any account name to view its level progression chart
            </p>
            <div className="flex items-center gap-6 flex-wrap mb-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 font-medium">
                  Level:
                </span>
                {isUnknownLevelMixRow(modalAccountsData.level) ? (
                  <span
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
                    style={{
                      backgroundColor: "#F3F4F6",
                      color: "#4B5563",
                    }}
                  >
                    Unknown
                  </span>
                ) : (
                  <Badge
                    label={getLevelCompactName(modalAccountsData.level)}
                    type={getLevelBadgeColor(modalAccountsData.level) as any}
                  />
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">
                  Number Of Accounts:
                </span>
                <span className="text-sm font-semibold text-gray-900">
                  {modalAccountsData.count || 0}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">
                  % of Accounts:
                </span>
                <span className="text-sm font-semibold text-gray-900">
                  {modalAccountsData.percentage !== null
                    ? `${modalAccountsData.percentage}%`
                    : "-"}
                </span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Account Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Last Assessment Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Last Published At
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {modalAccountsData.displayItems.map(
                    (displayItem: any, idx: number) => {
                      const mostRecent =
                        displayItem.allOccurrences?.length > 0
                          ? displayItem.allOccurrences[0]
                          : null;
                      const lastAssessmentName =
                        normalizeTeamAssessmentDisplayName(
                          mostRecent?.assessmentName,
                          "N/A"
                        );
                      const lastPublishedAt =
                        mostRecent?.publishedAt || displayItem.mostRecentDate;
                      return (
                        <tr
                          key={idx}
                          className="hover:bg-gray-50 border-b border-gray-100"
                        >
                          <td className="px-6 py-4 align-middle">
                            <button
                              onClick={() => {
                                handleAccountClick(displayItem);
                                setShowAccountsModal(false);
                                setModalAccountsData(null);
                              }}
                              className="text-sm text-blue-600 hover:text-blue-800 font-semibold cursor-pointer transition-colors text-left hover:underline inline-flex items-center gap-1.5"
                            >
                              {incompleteAccountIds.has(
                                String(displayItem.accountId || "")
                              ) ? (
                                <>
                                  {displayItem.accountName}{" "}
                                  <span className="text-amber-500">⚠</span>
                                </>
                              ) : (
                                displayItem.accountName
                              )}
                            </button>
                          </td>
                          <td className="px-6 py-4 align-middle">
                            <div className="text-sm text-gray-900">
                              {lastAssessmentName}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap align-middle">
                            <div className="text-sm text-gray-900">
                              {lastPublishedAt
                                ? formatDate(lastPublishedAt)
                                : "N/A"}
                            </div>
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CommonModal>

      {/* ── Chart Modal — Timeline View ── */}
      <CommonModal
        isOpen={showChartModal}
        toggle={() => {
          setShowChartModal(false);
          setChartData(null);
          setChartAccountName("");
        }}
        size="4xl"
        title={chartAccountName}
      >
        {chartData &&
          chartData.length > 0 &&
          (() => {
            const sortedData = [...chartData].sort(
              (a, b) => a.timestamp - b.timestamp
            );
            const firstPoint = sortedData[0];
            const lastPoint = sortedData[sortedData.length - 1];

            const formatDateTimeLocal = (dateString: string): string => {
              if (!dateString) return "";
              const formatted = formatAssessmentDateTimeUtc(dateString);
              if (!formatted) return dateString;
              // Timeline modal previously showed seconds; keep date+time UTC without seconds.
              return formatted;
            };

            const timelineData = {
              from: {
                level: firstPoint.level,
                date: formatDateTimeLocal(firstPoint.publishedAt),
                publishedAt: firstPoint.publishedAt,
                assessmentName: firstPoint.assessmentName,
              },
              to: {
                level: lastPoint.level,
                date: formatDateTimeLocal(lastPoint.publishedAt),
                publishedAt: lastPoint.publishedAt,
                assessmentName: lastPoint.assessmentName,
              },
            };

            return (
              <div className="level-mix-timeline-container">
                <TimelineGrid
                  accountName={chartAccountName}
                  timelineData={timelineData}
                  allDataPoints={chartData}
                  formatDateFn={formatDateTimeLocal}
                  organizationLevels={organizationLevels}
                />
              </div>
            );
          })()}
      </CommonModal>

      {/* ── Consolidated Report Modal ── */}
      <ConsolidatedReportModal
        isOpen={showSummaryModal}
        assessment={summaryAssessment as any}
        onClose={() => {
          setShowSummaryModal(false);
          setSummaryAssessment(null);
        }}
      />

      <StartFormalRoundModal
        isOpen={formalModalOpen}
        onClose={closeFormalModal}
        targets={formalTargets}
        name={formalName}
        description={formalDescription}
        dueOn={formalDueOn}
        isSubmitting={isFormalSubmitting}
        onNameChange={setFormalName}
        onDescriptionChange={setFormalDescription}
        onDueOnChange={setFormalDueOn}
        onConfirm={handleConfirmFormal}
      />

      <FormalRunningConflictModal
        accounts={formalRunningConflict}
        accountNamesById={incompleteAccountNamesById}
        onClose={() => setFormalRunningConflict(null)}
      />
    </div>
  );
};

export default LevelMixContainer;
